import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { ArrowLeft, ListChecks, Home, ChevronLeft, ChevronRight, Camera, type LucideIcon, Codepen, ArrowRightSquare, ArrowLeftSquare, Ruler, Building, HousePlus, Users, Wind, Flame, ClipboardCheck, PackagePlus, LayoutGrid, Download, Zap, Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import type { CurrentPage } from '../../App';
import RetrofitSurveyForm from '../forms/RetrofitSurvey';
import RetrofitFloorPlan from '../forms/RetrofitFloorPlan';
import { OccupancyAssessmentFormSchemaVersion } from '../forms/OccupancyAssessmentFormSchemaVersion';
import { SiteAssessmentFormSchemaVersion } from '../forms/SiteAssessmentFormSchemaVersion';
import { PAS2030QuestionsFormSchemaVersion } from '../forms/PAS2030QuestionsFormSchemaVersion';
import { HeatingSystemFormSchemaVersion } from '../forms/HeatingSystemFormSchemaVersion';
import { VentilationConditionFormSchemaVersion } from '../forms/VentilationConditionFormSchemaVersion';
import { MainPropertyMeasurementsFormSchemaVersion } from '../forms/MainPropertyMeasurementsFormSchemaVersion';
import { RetrofitSignaturesFormSchemaVersion } from '../forms/RetrofitSignaturesFormSchemaVersion';
import { PropertyFrontFormSchemaVersion } from '../forms/PropertyFrontFormSchemaVersion';
import { ExtensionsFormSchemaVersion } from '../forms/ExtensionsFormSchemaVersion';
import VentilationConditionSurveyForm from '../forms/VentilationConditionSurveyForm';
import HeatingSystemForm from '../forms/HeatingSystemForm';
import RoomSurveyContainer from '../forms/RoomSurvey';
import PropertyFrontForm from '../forms/PropertyFrontForm';
import PropertyRearForm from '../forms/PropertyRearFormSchemaVersion';
import PropertyRightForm from '../forms/PropertyRightFormSchemaVersion';
import PropertyLeftForm from '../forms/PropertyLeftFormSchemaVersion';
import MainPropertyMeasurementsForm from '../forms/MainPropertyMeasurementsForm';
import ExtensionsForm from '../forms/ExtensionsForm';
import SiteAssessmentForm from '../forms/SiteAssessmentForm';
import MeasureSpecificationForm from '../forms/MeasureSpecificationForm';
import PAS2030QuestionsForm from '../forms/PAS2030QuestionsForm';
import MetersFormSchemaVersion from '../forms/MetersFormSchemaVersion';
import RetrofitSignaturesForm from '../forms/RetrofitSignaturesForm';
import RetrofitExport from '../forms/RetrofitExport';
import PropertyFrontIconSrc from '../../assets/20250603_2041_House with \'F\' Logo_remix_01jwvmyq2qerz9qqh5a2008cqd Background Removed.png'; // Updated icon image path
import PropertyRearIconSrc from '../../assets/B Background Removed.png'; // New icon image path
import { useAutosave } from '../../hooks/useAutosave';
import { useCentralizedPhotoMigration } from '../../hooks/useCentralizedPhotoMigration';
import AutosaveIndicator from '../AutosaveIndicator';
import RetrofitAddressInputModal, { type RetrofitAddressData } from '../RetrofitAddressInputModal';
import DynamicFormRenderer from '../DynamicForm/DynamicFormRenderer';
import { FormEditorPanel } from '../FormEditor/FormEditorPanel';
import FieldConfigModal from '../DynamicForm/FieldConfigModal';
import PageManagementModal from '../PageManagementModal';
import { DEFAULT_RETROFIT_TEMPLATE, type FormTemplate, type FieldSchema, type CustomPage } from '../../types/formSchema';
import * as LucideIcons from 'lucide-react';
import { saveFormTemplate, getOrCreateSurveyTemplate } from '../../utils/formTemplateStorage';
import { exposeFloorPlanAnalysis } from '../../utils/floorPlanSizeAnalysis';
import { exposeFloorPlanCleanup } from '../../utils/floorPlanElementsCleanup';
import { 
  type RetrofitSurveyData, 
  createDefaultRetrofitSurveyData, 
  loadRetrofitSurveyFromSupabase, 
  saveRetrofitSurveyToSupabase,
  partialUpdateRetrofitSurvey,
  getRetrofitSurveyAddress,
  generateRetrofitSurveyFilename,
  validateRetrofitSurveyData
} from '../../utils/retrofitSurveyStorage';
import { cleanupSurveyPhotos } from '../../utils/cleanupSurveyPhotos';
import { v4 as uuidv4 } from 'uuid';

// Update to support custom page IDs
export type SurveySectionId = string; // Allows both built-in and custom page IDs

// Built-in page IDs for type safety
export type BuiltInSectionId = 'initial_questionnaire' | 'floor_plan' | 'occupancy_assessment' | 'ventilation_condition' | 'heating_system' | 'site_assessment' | 'measure_specification' | 'pas2030_questions' | 'property_front' | 'property_rear' | 'property_right' | 'property_left' | 'room_details' | 'meters' | 'main_property_measurements' | 'extensions' | 'signatures' | 'export';

interface SurveySection {
  id: SurveySectionId;
  label: string;
  icon: React.FC<IconProps> | LucideIcon;
  isCustom?: boolean; // Flag to identify custom pages
}

interface RetrofitSurveyLayoutProps {
  onNavigate: (page: CurrentPage) => void;
  floorPlanId?: string;
  retrofitSurveyId?: string;
}

// Define a type for icon components (Lucide or custom)
interface IconProps {
  size?: string | number;
  className?: string;
}

// Wrapper component for the custom image icon
const CustomPropertyFrontIcon: React.FC<IconProps> = ({ size = 18, className = '' }) => (
  <img src={PropertyFrontIconSrc} alt="Property Front" style={{ width: size, height: size }} className={className} />
);

const CustomPropertyRearIcon: React.FC<IconProps> = ({ size = 18, className = '' }) => (
  <img src={PropertyRearIconSrc} alt="Property Rear" style={{ width: size, height: size }} className={className} />
);

const allSections: SurveySection[] = [
  { id: 'initial_questionnaire', label: 'Initial Questionnaire', icon: ListChecks },
  { id: 'property_front', label: 'Property Front', icon: CustomPropertyFrontIcon },
  { id: 'property_rear', label: 'Property Rear', icon: CustomPropertyRearIcon },
  { id: 'property_right', label: 'Property Right', icon: ArrowRightSquare },
  { id: 'property_left', label: 'Property Left', icon: ArrowLeftSquare },
  { id: 'floor_plan', label: 'Floor Plan', icon: LayoutGrid },
  { id: 'room_details', label: 'Room Details', icon: Codepen },
  { id: 'extensions', label: 'Extensions', icon: HousePlus },
  { id: 'meters', label: 'Meters', icon: Zap },
  { id: 'heating_system', label: 'Heating System', icon: Flame },
  { id: 'measure_specification', label: 'Measure Specification', icon: PackagePlus },
  { id: 'pas2030_questions', label: 'PAS 2030 Questions', icon: ClipboardCheck },
  { id: 'main_property_measurements', label: 'Main Measurements', icon: Ruler },
  { id: 'ventilation_condition', label: 'Ventilation & Condition', icon: Wind },
  { id: 'occupancy_assessment', label: 'Occupancy Assessment', icon: Users },
  { id: 'site_assessment', label: 'Site Assessment', icon: ClipboardCheck },
  { id: 'signatures', label: 'Signatures', icon: ClipboardCheck },
  { id: 'export', label: 'Export', icon: Download },
];

export default function RetrofitSurveyLayout({ 
  onNavigate, 
  floorPlanId, 
  retrofitSurveyId 
}: RetrofitSurveyLayoutProps) {
  console.log('[RetrofitSurveyLayout] Rendering with floorPlanId:', floorPlanId, 'retrofitSurveyId:', retrofitSurveyId);
  
  // Check subscription status for feature restrictions
  const { isStandardPlan } = useSubscription();
  const actualSurveyId = retrofitSurveyId || floorPlanId;
  
  const [surveyData, setSurveyData] = useState<RetrofitSurveyData>(createDefaultRetrofitSurveyData());
  const [currentSurveyId, setCurrentSurveyId] = useState<string | null>(null);
  
  // Generate tempSurveyId IMMEDIATELY using useMemo to avoid race conditions
  // This ensures photo uploads have a surveyId even before Initial Questionnaire is completed
  const initialTempId = useMemo(() => {
    if (!actualSurveyId) {
      const newId = uuidv4();
      console.log('[RetrofitSurveyLayout] 🆔 Generated immediate temp survey ID:', newId);
      return newId;
    }
    return null;
  }, [actualSurveyId]);
  
  const [tempSurveyId, setTempSurveyId] = useState<string | null>(initialTempId);
  const [currentSection, setCurrentSection] = useState<SurveySectionId>('initial_questionnaire');
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // Form builder edit mode (now opens side panel)
  const [formTemplate, setFormTemplate] = useState<FormTemplate>(DEFAULT_RETROFIT_TEMPLATE);
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<FieldSchema | null>(null);
  const [insertionPosition, setInsertionPosition] = useState<number | undefined>(undefined); // Position to insert new custom field

  // ✅ CRITICAL FIX: Use ref to always have latest surveyData for autosave
  // This prevents stale closure bug where field changes aren't saved
  const surveyDataRef = useRef(surveyData);
  
  // Keep ref in sync with state
  useEffect(() => {
    surveyDataRef.current = surveyData;
  }, [surveyData]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = useState(false);
  const [changedSections, setChangedSections] = useState<Set<string>>(new Set());
  const lastSavedDataRef = useRef<string>('');
  const [hasAddressBeenSet, setHasAddressBeenSet] = useState(false);
  const [isFloorPlanFullscreen, setIsFloorPlanFullscreen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isFloorPlanModalOpen, setIsFloorPlanModalOpen] = useState(false);
  const isProcessingFloorPlanUpdateRef = useRef(false);
  const saveInProgressRef = useRef(false);
  const floorPlanRef = useRef<any>(null); // Ref to access RetrofitFloorPlan methods
  
  // Additional state for property form management
  const [buildForm, setBuildForm] = useState<string>('');
  const [exposedSide, setExposedSide] = useState<'left' | 'right' | 'both' | 'none' | ''>('');

  // Page management state
  const [isPageModalOpen, setIsPageModalOpen] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);

  const surveyTitle = "Retrofit Survey";

  // Semantic section definitions for built-in fields (matches form display)
  const getBuiltInFieldSections = (sectionId: string) => {
    const sectionDefinitions: Record<string, Array<{ id: string; title: string; isBuiltIn: true; fields: string[] }>> = {
      initial_questionnaire: [
        {
          id: 'survey_info',
          title: 'Survey Information',
          isBuiltIn: true,
          fields: ['surveyDate', 'surveyorName']
        },
        {
          id: 'customer_details',
          title: 'Customer Details',
          isBuiltIn: true,
          fields: ['customerFullName', 'customerEmail', 'customerMobile']
        },
        {
          id: 'property_address',
          title: 'Property Address',
          isBuiltIn: true,
          fields: ['buildingName', 'houseNumberName', 'town', 'postCode']
        },
        {
          id: 'property_details',
          title: 'Property Details',
          isBuiltIn: true,
          fields: ['tenure', 'buildingAge', 'dwellingType', 'majorityWallType']
        },
        {
          id: 'flat_details',
          title: 'Flat Details',
          isBuiltIn: true,
          fields: ['flatFloorNumber', 'corridorHeating']
        },
        {
          id: 'additional',
          title: 'Additional Notes',
          isBuiltIn: true,
          fields: ['additionalNotes']
        }
      ],
      property_front: [
        {
          id: 'build_detachment',
          title: 'Build Form & Detachment',
          isBuiltIn: true,
          fields: ['buildForm', 'exposedSide', 'detachmentType', 'buildingOrientation', 'orientationImage']
        },
        {
          id: 'front_elevation',
          title: 'Front Elevation',
          isBuiltIn: true,
          fields: ['frontElevationImage']
        },
        {
          id: 'external_wall',
          title: 'External Wall Details',
          isBuiltIn: true,
          fields: ['externalWallType', 'externalWallCondition', 'hasWallDefects', 'wallDefectImages']
        },
        {
          id: 'roof',
          title: 'Roof Details',
          isBuiltIn: true,
          fields: ['roofType', 'roofCondition', 'roofTypeEvidenceImages', 'hasExternalRoofDefects', 'externalRoofDefectsImages']
        },
        {
          id: 'guttering',
          title: 'Guttering & Drainage',
          isBuiltIn: true,
          fields: ['gutteringCondition', 'gutteringDrainageImages']
        },
        {
          id: 'wall_construction',
          title: 'Wall Construction',
          isBuiltIn: true,
          fields: ['propertyWallConstructionType', 'propertyMainConstructionWallType', 'wallThickness', 'wallTypeEvidenceImage', 'wallThicknessFromDoor', 'wallThicknessFromWindow', 'isWallInsulated']
        },
        {
          id: 'floor',
          title: 'Floor Details',
          isBuiltIn: true,
          fields: ['floorConstructionType', 'floorConstructionEvidenceImages', 'adequateSubFloorVentilation', 'evidenceOfFloorDampness']
        },
        {
          id: 'dpc',
          title: 'DPC (Damp Proof Course)',
          isBuiltIn: true,
          fields: ['dpcPresent', 'dpcVisible', 'dpcEvidenceImages']
        },
        {
          id: 'notes_front',
          title: 'Additional Notes',
          isBuiltIn: true,
          fields: ['additionalNotes']
        }
      ],
      property_rear: [
        {
          id: 'rear_elevation',
          title: 'Rear Elevation',
          isBuiltIn: true,
          fields: ['rearElevationImage', 'roofTypeEvidenceImages']
        },
        {
          id: 'backyard',
          title: 'Backyard',
          isBuiltIn: true,
          fields: ['hasBackyard', 'rearBackyardImages']
        },
        {
          id: 'guttering_rear',
          title: 'Guttering & Drainage',
          isBuiltIn: true,
          fields: ['gutteringDrainageImages']
        },
        {
          id: 'wall_rear',
          title: 'External Wall Details (Rear)',
          isBuiltIn: true,
          fields: ['externalWallType', 'externalWallCondition', 'wallTypeEvidenceImage', 'isWallInsulated', 'hasWallDefects', 'wallDefectImages']
        },
        {
          id: 'notes_rear',
          title: 'Additional Notes',
          isBuiltIn: true,
          fields: ['additionalNotes']
        }
      ],
      property_right: [
        {
          id: 'right_elevation',
          title: 'Right Side Elevation',
          isBuiltIn: true,
          fields: ['rightSideImages', 'roofTypeEvidenceImages']
        },
        {
          id: 'guttering_right',
          title: 'Guttering & Drainage',
          isBuiltIn: true,
          fields: ['gutteringDrainageImages']
        },
        {
          id: 'wall_right',
          title: 'External Wall Details (Right)',
          isBuiltIn: true,
          fields: ['externalWallType', 'externalWallCondition', 'wallTypeEvidenceImage', 'isWallInsulated', 'hasWallDefects', 'wallDefectImages']
        },
        {
          id: 'notes_right',
          title: 'Additional Notes',
          isBuiltIn: true,
          fields: ['additionalNotes']
        }
      ],
      property_left: [
        {
          id: 'left_elevation',
          title: 'Left Side Elevation',
          isBuiltIn: true,
          fields: ['leftSideImages', 'roofTypeEvidenceImages']
        },
        {
          id: 'guttering_left',
          title: 'Guttering & Drainage',
          isBuiltIn: true,
          fields: ['gutteringDrainageImages']
        },
        {
          id: 'wall_left',
          title: 'External Wall Details (Left)',
          isBuiltIn: true,
          fields: ['externalWallType', 'externalWallCondition', 'wallTypeEvidenceImage', 'isWallInsulated', 'hasWallDefects', 'wallDefectImages']
        },
        {
          id: 'notes_left',
          title: 'Additional Notes',
          isBuiltIn: true,
          fields: ['additionalNotes']
        }
      ],
      extensions: [
        {
          id: 'number_of_extensions',
          title: 'Number of Extensions',
          isBuiltIn: true,
          fields: ['numberOfExtensions']
        },
        {
          id: 'conservatory',
          title: 'Conservatory',
          isBuiltIn: true,
          fields: ['hasConservatory', 'conservatoryExternalElevationImages', 'conservatoryInternalImages']
        }
      ],
      meters: [
        {
          id: 'gas_meter',
          title: 'Gas Meter',
          isBuiltIn: true,
          fields: ['hasGasMeter', 'gasMeterLocation', 'gasMeterEarthBonding']
        },
        {
          id: 'electric_meter',
          title: 'Electric Meter',
          isBuiltIn: true,
          fields: ['hasElectricMeter', 'electricMeterLocation', 'electricMeter']
        },
        {
          id: 'consumer_board',
          title: 'Consumer Board (Fuse Box)',
          isBuiltIn: true,
          fields: ['hasConsumerBoard', 'consumerBoardLocation', 'consumerBoard']
        },
        {
          id: 'stop_tap',
          title: 'Stop Tap (Water Shut-off)',
          isBuiltIn: true,
          fields: ['hasStopTap', 'stopTapLocation', 'stopTap']
        }
      ],
      occupancy_assessment: [
        {
          id: 'household_composition',
          title: 'Household Composition',
          isBuiltIn: true,
          fields: ['numberOfOccupants', 'numberOfChildren', 'numberOfDisabled', 'numberOfElderly']
        },
        {
          id: 'sleeping_spaces',
          title: 'Sleeping Spaces',
          isBuiltIn: true,
          fields: ['singleBedSpaces', 'doubleBedSpaces']
        },
        {
          id: 'customer_documents',
          title: 'Customer Documents',
          isBuiltIn: true,
          fields: ['hasCustomerDocuments', 'customerDocumentsReason', 'customerDocumentsPhotos']
        }
      ],
      site_assessment: [
        {
          id: 'risk_assessment',
          title: 'Risk Assessment',
          isBuiltIn: true,
          fields: ['drillingConcerns', 'aggressivePeoplePresent', 'rcd', 'loftBoarding', 'petsIdentified', 'environmentalImpactAssessed', 'batsNestingBirdsPresent', 'fishProtected', 'hazardsForIndividuals', 'knotweedPresent', 'protectedSpeciesInLoft', 'finlockGutters', 'accessAroundProperty', 'verminEvidence', 'protectedWildlifeEvidence', 'scaffoldingErected', 'additionalLightingRequired', 'electricalPowerAvailable', 'gasSupplyRisk', 'animalSignsOnApproach', 'foulWaterPointsAvailable', 'pathsDrivewaysGoodOrder', 'goodWorkingEnvironment', 'overheadCablesPresent', 'hasGarage', 'hasPorch', 'hasOutsideTap']
        },
        {
          id: 'working_at_heights',
          title: 'Working at Heights',
          isBuiltIn: true,
          fields: ['conservatoryLadderRequired', 'loftWalkBoardsRequired', 'loftGuardrailsRequired', 'loftLightingRequired', 'specialistScaffoldingRequired', 'ladderRequired', 'ladderMatsRequired', 'catLadderRequired', 'tetraAndRunningLineRequired', 'tetraHarnessRequired', 'flatRoofLanyardRequired', 'flatRoofProtectionRequired']
        },
        {
          id: 'asbestos_form',
          title: 'Asbestos Form',
          isBuiltIn: true,
          fields: ['existingAsbestosReports', 'knownAsbestos', 'asbestosIndicated', 'asbestosSuspected', 'asbestosPresent']
        },
        {
          id: 'health_safety',
          title: 'Health and Safety',
          isBuiltIn: true,
          fields: ['worksCompleteStandardsInformed', 'glovesWipedDown', 'wasteDisposedCorrectly', 'handsWashed', 'dailyWasteDisposed']
        }
      ],
      pas2030_questions: [
        {
          id: 'heating_system',
          title: 'PAS 2030 - Boiler / Heating System',
          isBuiltIn: true,
          fields: ['heatingSystem_buildingFabric', 'heatingSystem_electricalInstallation', 'heatingSystem_buildingRegs', 'heatingSystem_gasSafety', 'heatingSystem_manufacturer', 'heatingSystem_plumeNuisance', 'heatingSystem_condensate', 'heatingSystem_hardWater', 'heatingSystem_waterConditioning']
        },
        {
          id: 'iwi_questions',
          title: 'PAS 2030 - IWI (Internal Wall Insulation)',
          isBuiltIn: true,
          fields: ['iwi_preDesignSurvey', 'iwi_thermalCalculations', 'iwi_condensationRisk', 'iwi_ventilationReqs', 'iwi_systemComponents', 'iwi_interfaces', 'iwi_practical', 'iwi_buildingRegs', 'iwi_moistureResistance', 'iwi_thermalBridging', 'iwi_combustionAppliances', 'iwi_ventilationDucts', 'iwi_services', 'iwi_supplierCompliance', 'iwi_substrateCondition', 'iwi_nonComplianceConfirmation', 'iwi_designAmendment']
        },
        {
          id: 'ewi_questions',
          title: 'PAS 2030 - EWI (External Wall Insulation)',
          isBuiltIn: true,
          fields: ['ewi_preDesignSurvey', 'ewi_thermalCalculations', 'ewi_condensationRisk', 'ewi_ventilationReqs', 'ewi_systemComponents', 'ewi_interfaces', 'ewi_practical', 'ewi_buildingRegs', 'ewi_moistureResistance', 'ewi_thermalBridging', 'ewi_combustionAppliances', 'ewi_ventilationDucts', 'ewi_services', 'ewi_supplierCompliance', 'ewi_substrateCondition', 'ewi_nonComplianceConfirmation', 'ewi_designAmendment', 'ewi_cavityWallConfirmation']
        },
        {
          id: 'cwi_questions',
          title: 'PAS 2030 - CWI (Cavity Wall Insulation)',
          isBuiltIn: true,
          fields: ['cwi_combustionAppliances', 'cwi_ventilationDucts', 'cwi_services', 'cwi_supplierReqs', 'cwi_buildingStructure', 'cwi_cavityExtent', 'cwi_siteLayout']
        },
        {
          id: 'rir_questions',
          title: 'PAS 2030 - RIR (Room in Roof)',
          isBuiltIn: true,
          fields: ['rirInsulation_roofVentilation', 'rirInsulation_thermalBridging']
        }
      ],
      heating_system: [
        {
          id: 'main_heating',
          title: 'Main Heating Source',
          isBuiltIn: true,
          fields: ['mainHeatingSource']
        },
        {
          id: 'boiler_details',
          title: 'Boiler Information',
          isBuiltIn: true,
          fields: ['boilerManufacturer', 'boilerBrand', 'modelQualifier', 'boilerType', 'fuelType', 'sapWinterEfficiency', 'boilerAge', 'commissionYear', 'serialNumber', 'boilerLocation', 'boilerPosition', 'boilerPositionOther', 'heatingSystemCondition']
        },
        {
          id: 'boiler_photos',
          title: 'Boiler Photos',
          isBuiltIn: true,
          fields: ['currentBoilerImage', 'boilerDataBadgeImage', 'fluePipeImage']
        },
        {
          id: 'additional_equipment',
          title: 'Additional Equipment',
          isBuiltIn: true,
          fields: ['hasCondensatePipe', 'condensatePipeImage', 'hasOilLpgTank', 'oilLpgTankImage', 'hasRenewables', 'renewablesImage', 'hasCylinderWaterHeating', 'cylinderWaterHeatingImage', 'hasOtherBoilers', 'otherBoilersDetails']
        },
        {
          id: 'heating_controls',
          title: 'Heating Controls',
          isBuiltIn: true,
          fields: ['fullSetFunctioningControls', 'preExistingHeatingControlsImages', 'systemHasBypassRadiator', 'bypassRadiatorImage']
        }
      ],
      ventilation_condition: [
        {
          id: 'room_counts',
          title: 'Room Information',
          isBuiltIn: true,
          fields: ['habitableRooms', 'heatedHabitableRooms', 'totalRooms']
        },
        {
          id: 'windows',
          title: 'Windows',
          isBuiltIn: true,
          fields: ['windowFramesCondition', 'draughtProofingCondition', 'windowFrameType', 'numberOfWindows', 'numberOfTrickleVents', 'windowGlazingType', 'windowGlazingMeasurement', 'windowGlazingThicknessImage']
        },
        {
          id: 'doors',
          title: 'External Doors',
          isBuiltIn: true,
          fields: ['numberOfExternalDoors', 'externalDoorsCondition', 'externalDoorsType', 'draughtProofingOnExternalDoor', 'doorUndercutsPresent', 'roomsWithNoDoorUndercuts', 'externalDoorsImages', 'frontDoorOpenImage']
        },
        {
          id: 'ventilation',
          title: 'Ventilation & Fans',
          isBuiltIn: true,
          fields: ['numberOfWetRooms', 'numberOfVentilationFans', 'roomsMissingVentilationFans', 'ventilationTypePresent']
        },
        {
          id: 'roof',
          title: 'Roof Condition',
          isBuiltIn: true,
          fields: ['roofType', 'externalRoofCondition', 'visibleExternalRoofDefects', 'roofDefectImages']
        },
        {
          id: 'internal_walls',
          title: 'Internal Walls',
          isBuiltIn: true,
          fields: ['internalWallCondition', 'evidenceOfDamp', 'dampnessImages']
        },
        {
          id: 'floor',
          title: 'Floor Construction',
          isBuiltIn: true,
          fields: ['floorConstructionType', 'adequateSubFloorVentilation', 'evidenceOfFloorDampness']
        },
        {
          id: 'external_walls',
          title: 'External Walls',
          isBuiltIn: true,
          fields: ['externalWallType', 'externalWallCondition', 'additionalVisibleWallDefects', 'wallDefectImages']
        }
      ],
      main_property_measurements: [
        {
          id: 'property_overview',
          title: 'Property Overview',
          isBuiltIn: true,
          fields: ['numberOfStoreys', 'totalArea']
        },
        {
          id: 'cellar_details',
          title: 'Cellar Information',
          isBuiltIn: true,
          fields: ['hasCellar', 'cellarLength', 'cellarWidth', 'cellarArea', 'cellarPhotos']
        }
      ],
      signatures: [
        {
          id: 'pre_signature_questions',
          title: 'Pre-Installation Assessment',
          isBuiltIn: true,
          fields: ['asbestosChecksUndertaken', 'servicesAffectedByInstallation', 'potentialRiskToServices', 'installationInLineWithDesign', 'methodStatementSuitable', 'protectedSpeciesDisturbance', 'customerUnderstandsWork', 'customerAwareOfDisruption', 'customerHasRequests', 'siteAccessArrangementsAdequate', 'otherInstallationInfo', 'potentialMoistureBuildUp']
        },
        {
          id: 'customer_signatures',
          title: 'Customer Declarations',
          isBuiltIn: true,
          fields: ['customerStatement1', 'customerStatement1Date', 'customerStatement2', 'customerStatement2Date', 'customerStatement3', 'customerStatement3Date', 'customerStatement4', 'customerStatement4Date']
        },
        {
          id: 'surveyor_signatures',
          title: 'Surveyor Sign Off',
          isBuiltIn: true,
          fields: ['surveyorStatement1', 'surveyorStatement1Date', 'surveyorStatement2', 'surveyorStatement2Date', 'surveyorStatement3', 'surveyorStatement3Date', 'surveyorStatement4', 'surveyorStatement4Date', 'surveyorStatement5', 'surveyorStatement5Date']
        },
        {
          id: 'feedback',
          title: 'Survey Feedback',
          isBuiltIn: true,
          fields: ['feedbackRating', 'feedbackComments']
        }
      ],
      measure_specification: [
        {
          id: 'suitable_measures',
          title: 'Suitable Measures',
          isBuiltIn: true,
          fields: ['suitableMeasures', 'wallInsulationType', 'wallInsulationElevationsCount']
        },
        {
          id: 'loft_survey_main',
          title: 'Loft Survey - Main Property',
          isBuiltIn: true,
          fields: ['mainPropertyRoofType', 'loftNeedsInsulating', 'loftHasExistingInsulation', 'mainLoftDepthOfInsulation', 'mainPropertyInsulationDepthEvidence', 'accessToAllAreasOfLoft', 'inaccessibleAreaM2', 'loftHatchAlreadyPresent', 'isThereALoftLadder', 'isTheLoftHatchAlreadyInsulated', 'isTheLoftHatchDraughtProofed', 'loftHatchLengthMM', 'loftHatchWidthMM', 'mainLoftHatchEvidence', 'mainPropertyLoftLength', 'mainPropertyLoftWidth', 'mainPropertyLoftHeight', 'mainPropertyRoofPitch', 'mainPropertyRoofPitchEvidence', 'walkboardsRequired', 'tenantsBelongingsToBeRemoved', 'explainedToOccupier', 'loftDownlightsPresent', 'downlightCoversRequired', 'downlightImage', 'coldWaterTankPresent', 'pipeworkPresent', 'coldWaterTankPipeworkNeedInsulation', 'coldWaterTankPipeworkEvidence', 'roofVentsPresent', 'roofVentsImage', 'verticalFlueInLoft', 'highCurrentCablesPresent', 'mainLoftAreaImages', 'numberOfExtensions']
        },
        {
          id: 'heating_controls',
          title: 'Heating Controls',
          isBuiltIn: true,
          fields: ['fullSetFunctioningControls', 'roomThermostat', 'boilerProgrammer', 'smartThermostat', 'systemHasBypassRadiator', 'preExistingHeatingControlsImages', 'bypassRadiatorImage']
        },
        {
          id: 'heating_existing_boiler',
          title: 'Heating System - Existing Boiler',
          isBuiltIn: true,
          fields: ['isBoilerPresent', 'boilerManufacturerName', 'boilerBrand', 'modelQualifier', 'boilerType', 'fuelType', 'sapWinterEfficiency', 'ageOfBoiler', 'yearOfCommissioning', 'serialNumber', 'boilerLocation', 'boilerPosition', 'anyOtherBoilers', 'otherBoilersDetails', 'isCondensatePipe', 'condensatePipeTermination', 'imageOfCurrentBoiler', 'boilerDataBadge', 'imageOfFluePipe', 'imageOfCondensatePipe']
        },
        {
          id: 'heating_new_system',
          title: 'Heating System - New System',
          isBuiltIn: true,
          fields: ['newSystemHeat100Percent', 'postHeatingSystem', 'flueType', 'flueGuardRequired', 'flueFittingKitRequired', 'plumeKitRequired', 'frostProtectionRequired', 'condenseTermination', 'gasMeterBoilerDistance', 'remedialWorksToBrickwork', 'newBoilerLocation', 'weatherCompensatorNeeded', 'loadCompensatorNeeded', 'trvsRequired', 'lockShieldsRequired', 'coldWaterPressureAdequate', 'mainsBoardLocation', 'electricityMeterLocation', 'stopTapLocation', 'stopTapImages', 'propertyEarthBondingPresent', 'fuseSpurPresent', 'pipeworkInUnheatedAreas']
        },
        {
          id: 'rir_survey_main',
          title: 'Room in Roof Survey',
          isBuiltIn: true,
          fields: ['buildingType', 'rirRoomsCount', 'rirFloorArea', 'rirResidualLoftArea', 'roofPitchRIR', 'roofPitchEvidenceRIR', 'internalSurfacesDamp', 'needsInsulating', 'downlightsPresent', 'downlightsEvidence', 'rirFreeOfStoredItems', 'plumberRequired', 'electricianRequired', 'skirtingBoardsRequired', 'skirtingBoardLength', 'walkwayRequired', 'workingTanksPipesPresent', 'fixedStairCase', 'dormerPresent', 'rirExteriorElevations', 'rirInternalRoomPhotos']
        },
        {
          id: 'rir_stud_walls',
          title: 'RIR - Stud Walls',
          isBuiltIn: true,
          fields: ['studWallsPresent', 'studWallConstruction', 'studWallTotalArea', 'studWallElementInsulated', 'studWallRequiresInsulation', 'studWallInstallationMethod', 'studWallInsulationType', 'studWall100PercentInstalled', 'studWallInsulationThickness', 'studWallImages']
        },
        {
          id: 'rir_sloping_ceilings',
          title: 'RIR - Sloping Ceilings',
          isBuiltIn: true,
          fields: ['slopingCeilingsPresent', 'slopingCeilingArea', 'slopingCeilingElementInsulated', 'slopingCeilingRequiresInsulation', 'slopingCeilingInstallationMethod', 'slopingCeilingInsulationType', 'slopingCeiling100PercentInstalled', 'slopingCeilingInsulationThickness', 'slopingCeilingImages']
        },
        {
          id: 'rir_gable_walls',
          title: 'RIR - Gable Walls',
          isBuiltIn: true,
          fields: ['gableWallsPresent', 'gableWallConstruction', 'gableWallTotalArea', 'gableWallElementInsulated', 'gableRequiresInsulation', 'gableWallInstallationMethod', 'gableWallInsulationType', 'gableWall100PercentInstalled', 'gableWallInsulationThickness', 'gableWallImages']
        },
        {
          id: 'rir_party_walls',
          title: 'RIR - Party Walls',
          isBuiltIn: true,
          fields: ['partyWallsPresent', 'partyWallConstruction', 'partyWallTotalArea', 'partyWallElementInsulated', 'partyWallRequiresInsulation', 'partyWallInstallationMethod', 'partyWallInsulationType', 'partyWall100PercentInstalled', 'partyWallInsulationThickness', 'partyWallEvidence']
        },
        {
          id: 'rir_dormer_windows',
          title: 'RIR - Dormer Windows',
          isBuiltIn: true,
          fields: ['dormerWindowsPresent', 'dormerWindowCount', 'dormerCeilingTotalArea', 'dormerRevealsTotalArea', 'dormerElementInsulated', 'dormerWindowsRequiresInsulation', 'dormerWindowInstallationMethod', 'dormerWindowInsulationType', 'dormerWindow100PercentInstalled', 'dormerWindowInsulationThickness', 'dormerWindowsImages']
        },
        {
          id: 'rir_residual_areas',
          title: 'RIR - Residual Areas',
          isBuiltIn: true,
          fields: ['residualAreasPresent', 'residualLoftSpace', 'existingInsulationPresent', 'existingInsulationThickness', 'residualAreasToBeInsulated', 'residualAreaImages']
        },
        {
          id: 'rir_flat_ceilings',
          title: 'RIR - Flat Ceilings',
          isBuiltIn: true,
          fields: ['flatCeilingPresent', 'totalAreaFlatCeiling', 'flatCeilingElementInsulated', 'flatCeilingRequiresInsulation', 'accessAboveFlatCeiling', 'flatCeilingInstallationMethod', 'flatCeilingInsulationType', 'flatCeiling100PercentInstalled', 'flatCeilingImage']
        }
      ],
    };
    
    console.log('[getBuiltInFieldSections] Section:', sectionId, 'Sections:', sectionDefinitions[sectionId]);
    return sectionDefinitions[sectionId] || [];
  };

  // Built-in field definitions for each section (for editor panel)
  const getBuiltInFieldDefinitions = (sectionId: string) => {
    const fieldDefinitions: Record<string, Array<{ id: string; label: string; type: string; required: boolean }>> = {
      initial_questionnaire: [
        { id: 'surveyDate', label: 'Survey Date', type: 'date', required: true },
        { id: 'surveyorName', label: 'Surveyor name', type: 'text', required: true },
        { id: 'customerFullName', label: 'Customer Full name', type: 'text', required: true },
        { id: 'customerEmail', label: 'Customer Email', type: 'text', required: true },
        { id: 'customerMobile', label: 'Customer Mobile Number', type: 'text', required: true },
        { id: 'buildingName', label: 'Building / Flat Name & Number', type: 'text', required: false },
        { id: 'houseNumberName', label: 'House Number & Street', type: 'text', required: true },
        { id: 'town', label: 'Town', type: 'text', required: true },
        { id: 'postCode', label: 'Post Code', type: 'text', required: true },
        { id: 'tenure', label: 'Tenure', type: 'dropdown', required: true },
        { id: 'buildingAge', label: 'Building Age', type: 'dropdown', required: true },
        { id: 'dwellingType', label: 'Dwelling Type', type: 'dropdown', required: true },
        { id: 'flatFloorNumber', label: 'Floor Number (for flats)', type: 'number', required: false },
        { id: 'corridorHeating', label: 'Corridor Heating (for flats)', type: 'dropdown', required: false },
        { id: 'majorityWallType', label: 'Majority Wall Type', type: 'dropdown', required: false },
        { id: 'additionalNotes', label: 'Additional Notes', type: 'textarea', required: false },
      ],
      property_front: [
        { id: 'buildForm', label: 'Build Form', type: 'dropdown', required: true },
        { id: 'exposedSide', label: 'Exposed Side', type: 'dropdown', required: false },
        { id: 'detachmentType', label: 'Detachment Type Photo', type: 'photo', required: false },
        { id: 'buildingOrientation', label: 'Building Orientation', type: 'text', required: true },
        { id: 'orientationImage', label: 'Orientation Photo', type: 'photo', required: false },
        { id: 'frontElevationImage', label: 'Front Elevation', type: 'photo', required: true },
        { id: 'externalWallType', label: 'External Wall Type', type: 'dropdown', required: true },
        { id: 'externalWallCondition', label: 'Condition of External Wall', type: 'dropdown', required: true },
        { id: 'hasWallDefects', label: 'Additional Visible Wall Defects?', type: 'boolean', required: true },
        { id: 'wallDefectImages', label: 'Wall Defect Images', type: 'photo', required: false },
        { id: 'roofType', label: 'Roof Type', type: 'dropdown', required: true },
        { id: 'roofCondition', label: 'Roof Condition', type: 'dropdown', required: true },
        { id: 'roofTypeEvidenceImages', label: 'Roof Type Evidence', type: 'photo', required: true },
        { id: 'hasExternalRoofDefects', label: 'External Roof Defects', type: 'boolean', required: true },
        { id: 'externalRoofDefectsImages', label: 'External Roof Defects Images', type: 'photo', required: false },
        { id: 'gutteringCondition', label: 'Guttering Condition', type: 'dropdown', required: true },
        { id: 'gutteringDrainageImages', label: 'Guttering & Drainage Images', type: 'photo', required: true },
        { id: 'propertyWallConstructionType', label: 'Wall Construction Type', type: 'dropdown', required: true },
        { id: 'propertyMainConstructionWallType', label: 'Main Construction Wall Type', type: 'dropdown', required: false },
        { id: 'wallThickness', label: 'Wall Thickness', type: 'text', required: false },
        { id: 'wallTypeEvidenceImage', label: 'Wall Type Evidence', type: 'photo', required: false },
        { id: 'wallThicknessFromDoor', label: 'Wall Thickness from Door', type: 'photo', required: false },
        { id: 'wallThicknessFromWindow', label: 'Wall Thickness from Window', type: 'photo', required: false },
        { id: 'isWallInsulated', label: 'Is Wall Insulated?', type: 'boolean', required: false },
        { id: 'floorConstructionType', label: 'Floor Construction Type', type: 'dropdown', required: true },
        { id: 'floorConstructionEvidenceImages', label: 'Floor Construction Evidence', type: 'photo', required: false },
        { id: 'adequateSubFloorVentilation', label: 'Adequate Sub-Floor Ventilation?', type: 'boolean', required: false },
        { id: 'evidenceOfFloorDampness', label: 'Evidence of Floor Dampness?', type: 'boolean', required: false },
        { id: 'dpcPresent', label: 'DPC Present?', type: 'boolean', required: false },
        { id: 'dpcVisible', label: 'DPC Visible?', type: 'boolean', required: false },
        { id: 'dpcEvidenceImages', label: 'DPC Evidence Images', type: 'photo', required: false },
        { id: 'additionalNotes', label: 'Additional Notes', type: 'textarea', required: false },
      ],
      property_rear: [
        { id: 'rearElevationImage', label: 'Rear Elevation', type: 'photo', required: true },
        { id: 'roofTypeEvidenceImages', label: 'Roof Type Evidence', type: 'photo', required: true },
        { id: 'hasBackyard', label: 'Does Property Have a Backyard?', type: 'boolean', required: true },
        { id: 'rearBackyardImages', label: 'Rear Backyard Image', type: 'photo', required: false },
        { id: 'gutteringDrainageImages', label: 'Guttering and Drainage', type: 'photo', required: true },
        { id: 'externalWallType', label: 'External Wall Type (Rear)', type: 'dropdown', required: true },
        { id: 'externalWallCondition', label: 'Condition of External Wall (Rear)', type: 'dropdown', required: true },
        { id: 'wallTypeEvidenceImage', label: 'Wall Type Evidence (Rear)', type: 'photo', required: true },
        { id: 'isWallInsulated', label: 'Is Wall Insulated? (Rear)', type: 'boolean', required: true },
        { id: 'hasWallDefects', label: 'Additional Visible Wall Defects?', type: 'boolean', required: true },
        { id: 'wallDefectImages', label: 'Wall Defect Images (Rear)', type: 'photo', required: false },
        { id: 'additionalNotes', label: 'Additional Notes', type: 'textarea', required: false },
      ],
      property_right: [
        { id: 'rightSideImages', label: 'Right Side Images', type: 'photo', required: true },
        { id: 'roofTypeEvidenceImages', label: 'Roof Type Evidence (Right)', type: 'photo', required: true },
        { id: 'gutteringDrainageImages', label: 'Guttering and Drainage (Right)', type: 'photo', required: true },
        { id: 'externalWallType', label: 'External Wall Type (Right)', type: 'dropdown', required: true },
        { id: 'externalWallCondition', label: 'Condition of External Wall (Right)', type: 'dropdown', required: true },
        { id: 'wallTypeEvidenceImage', label: 'Wall Type Evidence (Right)', type: 'photo', required: true },
        { id: 'isWallInsulated', label: 'Is Wall Insulated? (Right)', type: 'boolean', required: true },
        { id: 'hasWallDefects', label: 'Additional Visible Wall Defects?', type: 'boolean', required: true },
        { id: 'wallDefectImages', label: 'Wall Defect Images (Right)', type: 'photo', required: false },
        { id: 'additionalNotes', label: 'Additional Notes', type: 'textarea', required: false },
      ],
      property_left: [
        { id: 'leftSideImages', label: 'Left Side Images', type: 'photo', required: true },
        { id: 'roofTypeEvidenceImages', label: 'Roof Type Evidence (Left)', type: 'photo', required: true },
        { id: 'gutteringDrainageImages', label: 'Guttering and Drainage (Left)', type: 'photo', required: true },
        { id: 'externalWallType', label: 'External Wall Type (Left)', type: 'dropdown', required: true },
        { id: 'externalWallCondition', label: 'Condition of External Wall (Left)', type: 'dropdown', required: true },
        { id: 'wallTypeEvidenceImage', label: 'Wall Type Evidence (Left)', type: 'photo', required: true },
        { id: 'isWallInsulated', label: 'Is Wall Insulated? (Left)', type: 'boolean', required: true },
        { id: 'hasWallDefects', label: 'Additional Visible Wall Defects?', type: 'boolean', required: true },
        { id: 'wallDefectImages', label: 'Wall Defect Images (Left)', type: 'photo', required: false },
        { id: 'additionalNotes', label: 'Additional Notes', type: 'textarea', required: false },
      ],
      extensions: [
        { id: 'numberOfExtensions', label: 'How many extensions does the property have?', type: 'dropdown', required: false },
        { id: 'hasConservatory', label: 'Does the property have a conservatory?', type: 'boolean', required: false },
        { id: 'conservatoryExternalElevationImages', label: 'Conservatory External Elevation Photos', type: 'photo', required: false },
        { id: 'conservatoryInternalImages', label: 'Conservatory Internal Photos', type: 'photo', required: false },
      ],
      meters: [
        { id: 'hasGasMeter', label: 'Does the property have a gas meter?', type: 'boolean', required: true },
        { id: 'gasMeterLocation', label: 'Gas Meter Location', type: 'text', required: false },
        { id: 'gasMeterEarthBonding', label: 'Gas Meter Earth Bonding Photos', type: 'photo', required: false },
        { id: 'hasElectricMeter', label: 'Does the property have an electric meter?', type: 'boolean', required: true },
        { id: 'electricMeterLocation', label: 'Electric Meter Location', type: 'text', required: false },
        { id: 'electricMeter', label: 'Electric Meter Photos', type: 'photo', required: false },
        { id: 'hasConsumerBoard', label: 'Does the property have a consumer board?', type: 'boolean', required: true },
        { id: 'consumerBoardLocation', label: 'Consumer Board Location', type: 'text', required: false },
        { id: 'consumerBoard', label: 'Consumer Board Photos', type: 'photo', required: false },
        { id: 'hasStopTap', label: 'Does the property have a stop tap?', type: 'boolean', required: true },
        { id: 'stopTapLocation', label: 'Stop Tap Location', type: 'text', required: false },
        { id: 'stopTap', label: 'Stop Tap Photos', type: 'photo', required: false },
      ],
      occupancy_assessment: [
        { id: 'numberOfOccupants', label: 'How many people live in the home?', type: 'text', required: true },
        { id: 'numberOfChildren', label: 'How many are children (under 16)?', type: 'text', required: true },
        { id: 'numberOfDisabled', label: 'How many are registered with disabilities?', type: 'text', required: true },
        { id: 'numberOfElderly', label: 'How many are elderly people (65 and over)?', type: 'text', required: true },
        { id: 'singleBedSpaces', label: 'Number of single bed spaces?', type: 'text', required: true },
        { id: 'doubleBedSpaces', label: 'Number of double bed spaces?', type: 'text', required: true },
        { id: 'hasCustomerDocuments', label: 'Customer Documents', type: 'boolean', required: true },
        { id: 'customerDocumentsReason', label: 'Reason why customer documents weren\'t taken', type: 'text', required: false },
        { id: 'customerDocumentsPhotos', label: 'Customer Documents Photos', type: 'photo', required: false },
      ],
      site_assessment: [
        // Risk Assessment (27 fields)
        { id: 'drillingConcerns', label: 'Are there any drilling concerns?', type: 'boolean', required: true },
        { id: 'aggressivePeoplePresent', label: 'Aggressive people present?', type: 'boolean', required: true },
        { id: 'rcd', label: 'RCD?', type: 'boolean', required: true },
        { id: 'loftBoarding', label: 'Loft boarding?', type: 'boolean', required: true },
        { id: 'petsIdentified', label: 'Spoken to customers to identify the presence of any pets', type: 'boolean', required: true },
        { id: 'environmentalImpactAssessed', label: 'Reviewed any environmental impact assessments associated', type: 'boolean', required: true },
        { id: 'batsNestingBirdsPresent', label: 'Checked for presence of bats, nesting birds?', type: 'boolean', required: true },
        { id: 'fishProtected', label: 'Protected fish in ponds from dust, insulation media or other hazardous substances', type: 'boolean', required: true },
        { id: 'hazardsForIndividuals', label: 'Any hazards that may affect this category of individuals?', type: 'boolean', required: true },
        { id: 'knotweedPresent', label: 'Japanese Knotweed or other invasive species present?', type: 'boolean', required: true },
        { id: 'protectedSpeciesInLoft', label: 'Protected species in loft?', type: 'boolean', required: true },
        { id: 'finlockGutters', label: 'Does the property have finlock gutters?', type: 'boolean', required: true },
        { id: 'accessAroundProperty', label: 'Access from road / around property?', type: 'boolean', required: true },
        { id: 'verminEvidence', label: 'Is there any evidence of vermin?', type: 'boolean', required: true },
        { id: 'protectedWildlifeEvidence', label: 'Is there any evidence of protected wildlife (ie bats)?', type: 'boolean', required: true },
        { id: 'scaffoldingErected', label: 'Scaffolding erected?', type: 'boolean', required: true },
        { id: 'additionalLightingRequired', label: 'Are there any additional lighting requirements needed at point of work?', type: 'boolean', required: true },
        { id: 'electricalPowerAvailable', label: 'Is an electrical power supply available?', type: 'boolean', required: true },
        { id: 'gasSupplyRisk', label: 'Any aspects of existing gas supply and distribution that pose a risk?', type: 'boolean', required: true },
        { id: 'animalSignsOnApproach', label: 'Checked for signs of animals on approaching property?', type: 'boolean', required: true },
        { id: 'foulWaterPointsAvailable', label: 'Are there foul water points available to drain system?', type: 'boolean', required: true },
        { id: 'pathsDrivewaysGoodOrder', label: 'Are the surrounding paths/driveways in good order?', type: 'boolean', required: true },
        { id: 'goodWorkingEnvironment', label: 'Does the property allow for a good working environment?', type: 'boolean', required: true },
        { id: 'overheadCablesPresent', label: 'Overhead electricity cables present?', type: 'boolean', required: true },
        { id: 'hasGarage', label: 'Does the property have a garage?', type: 'boolean', required: true },
        { id: 'hasPorch', label: 'Does the property have a porch?', type: 'boolean', required: true },
        { id: 'hasOutsideTap', label: 'Does the property have an outside tap?', type: 'boolean', required: true },
        // Working at Heights (12 fields)
        { id: 'conservatoryLadderRequired', label: 'Conservatory ladder required', type: 'boolean', required: false },
        { id: 'loftWalkBoardsRequired', label: 'Loft walk boards required', type: 'boolean', required: false },
        { id: 'loftGuardrailsRequired', label: 'Loft guardrails required', type: 'boolean', required: false },
        { id: 'loftLightingRequired', label: 'Loft lighting required', type: 'boolean', required: false },
        { id: 'specialistScaffoldingRequired', label: 'Specialist scaffolding required', type: 'boolean', required: false },
        { id: 'ladderRequired', label: 'Ladder required', type: 'boolean', required: false },
        { id: 'ladderMatsRequired', label: 'Ladder mats required', type: 'boolean', required: false },
        { id: 'catLadderRequired', label: 'Cat ladder required', type: 'boolean', required: false },
        { id: 'tetraAndRunningLineRequired', label: 'Tetra and running line required', type: 'boolean', required: false },
        { id: 'tetraHarnessRequired', label: 'Tetra harness required', type: 'boolean', required: false },
        { id: 'flatRoofLanyardRequired', label: 'Flat roof lanyard required', type: 'boolean', required: false },
        { id: 'flatRoofProtectionRequired', label: 'Flat roof protection required', type: 'boolean', required: false },
        // Asbestos (5 fields)
        { id: 'existingAsbestosReports', label: 'Does the client have any existing asbestos reports / documents?', type: 'boolean', required: false },
        { id: 'knownAsbestos', label: 'Has the client been asked if there is any known asbestos?', type: 'boolean', required: false },
        { id: 'asbestosIndicated', label: 'Has the client indicated there may be asbestos within the property?', type: 'boolean', required: false },
        { id: 'asbestosSuspected', label: 'Do you or the client have any reason to suspect asbestos may be present?', type: 'boolean', required: false },
        { id: 'asbestosPresent', label: 'Is there asbestos present?', type: 'boolean', required: false },
        // Health and Safety (5 fields)
        { id: 'worksCompleteStandardsInformed', label: 'I have informed the customer that the works are complete to the relevant standards', type: 'boolean', required: false },
        { id: 'glovesWipedDown', label: 'I have wiped down and operational rubber gloves, surfaces, tools and instruments used within the premises', type: 'boolean', required: false },
        { id: 'wasteDisposedCorrectly', label: 'I have removed and placed into a plastic bag, any wiped, disposable gloves and overalls used, whilst trying not to cross contaminate onto existing clothes or persons', type: 'boolean', required: false },
        { id: 'handsWashed', label: 'I have ensured that I have cleaned / washed my hands and where possible / appropriate any reusable PPE lie safety glasses), using soap and water, antibacterial wipes, or used suitable hand sanitiser on my hands before travelling to the next job', type: 'boolean', required: false },
        { id: 'dailyWasteDisposed', label: 'I have, at the end of each day, and following the appropriate local waste regulations, disposed of any bagged waste in line with existing business processes and / or Government guidance', type: 'boolean', required: false },
      ],
      pas2030_questions: [
        // Heating System (9 fields)
        { id: 'heatingSystem_buildingFabric', label: 'Is the condition of the building fabric satisfactory in relation to the proposed work?', type: 'boolean', required: false },
        { id: 'heatingSystem_electricalInstallation', label: 'Is the condition of the existing electrical installation satisfactory in relation to the proposed work?', type: 'boolean', required: false },
        { id: 'heatingSystem_buildingRegs', label: 'Will the installation work result in non-compliance with the Building Regulations in relation to workmanship; materials; structural stability; fire safety; resistance to moisture; heat-producing appliances; conservation of fuel and power', type: 'boolean', required: false },
        { id: 'heatingSystem_gasSafety', label: 'Will the installation work result in non-compliance with relevant gas safety regulations?', type: 'boolean', required: false },
        { id: 'heatingSystem_manufacturer', label: 'Will the proposed installation be compliant with any requirements stated by the boiler manufacturer?', type: 'boolean', required: false },
        { id: 'heatingSystem_plumeNuisance', label: 'Will/might the proposed installation result in a plume nuisance situation?', type: 'boolean', required: false },
        { id: 'heatingSystem_condensate', label: 'Are any special condensate disposal arrangements required?', type: 'boolean', required: false },
        { id: 'heatingSystem_hardWater', label: 'Is the dwelling located in a hard water area (above 200 ppm)?', type: 'boolean', required: false },
        { id: 'heatingSystem_waterConditioning', label: 'Are water conditioning arrangements required in relation to the water hardness?', type: 'boolean', required: false },
        // IWI (17 fields)
        { id: 'iwi_preDesignSurvey', label: 'A full and detailed pre-design building survey has been undertaken by a competent person, prior to the retrofit design being undertaken.', type: 'boolean', required: false },
        { id: 'iwi_thermalCalculations', label: 'Thermal performance calculations', type: 'boolean', required: false },
        { id: 'iwi_condensationRisk', label: 'Condensation risk analysis', type: 'boolean', required: false },
        { id: 'iwi_ventilationReqs', label: 'Ventilation requirements and standard/bespoke drawing details', type: 'boolean', required: false },
        { id: 'iwi_systemComponents', label: 'The main components of the system including the fixing type/method, the insulation type and thickness, the reinforcing coat and type of reinforcement and the finish', type: 'boolean', required: false },
        { id: 'iwi_interfaces', label: 'The proposed details for the main interfaces', type: 'boolean', required: false },
        { id: 'iwi_practical', label: 'The installation to the retrofit design is practical and achievable given the particular IWI system chosen for the project and the specific dwelling construction, site conditions and other EEMs planned for the property', type: 'boolean', required: false },
        { id: 'iwi_buildingRegs', label: 'Will the installation result in non-compliance with the Building Regulations e.g. in relation to workmanship; materials; structural ability; fire safety', type: 'boolean', required: false },
        { id: 'iwi_moistureResistance', label: 'Will the installation provide resistance to moisture.', type: 'boolean', required: false },
        { id: 'iwi_thermalBridging', label: 'Will the installation result in avoidable thermal bridging', type: 'boolean', required: false },
        { id: 'iwi_combustionAppliances', label: 'Will the installation result in the unsafe operation of combustion appliances', type: 'boolean', required: false },
        { id: 'iwi_ventilationDucts', label: 'Will the installation compromise the functionality of existing ventilation ducts/systems', type: 'boolean', required: false },
        { id: 'iwi_services', label: 'Will the installation compromise the functionality and/or safety of existing services (gas, electric, water, telephone, etc.)', type: 'boolean', required: false },
        { id: 'iwi_supplierCompliance', label: 'Will the installation result in the proposed installation being non-compliant with any requirements of the EEM supplier or of the retrofit design.', type: 'boolean', required: false },
        { id: 'iwi_substrateCondition', label: 'Confirmation that the condition of the substrate is suitable for the works to commence and where all or any of the substrate does not fulfil the requirements for installation, preparation of proposals for adaptations to be made or additional preparation undertaken that will be necessary in order that works can commence.', type: 'boolean', required: false },
        { id: 'iwi_nonComplianceConfirmation', label: 'Confirmation all instances of potential non-compliance identified in the pre-installation building inspection shall be documented and referred to the Retrofit Coordinator for resolution. Any design adjustments, special adaptations and/or additional preparation requirements shall be confirmed as acceptable in writing, by the system supplier and/or the Retrofit Coordinator.', type: 'boolean', required: false },
        { id: 'iwi_designAmendment', label: 'Confirmation the retrofit design documentation shall be amended to include any specified changes to the installation, the installation method statement modified accordingly and the pre-installation building inspection records updated to provide documentary evidence that the intended modified installation will address all the issues identified in the pre-installation building inspection and meets the requirements of all parties.', type: 'boolean', required: false },
        // EWI (18 fields)
        { id: 'ewi_preDesignSurvey', label: 'A full and detailed pre-design building survey has been undertaken by a competent person, prior to the retrofit design being undertaken.', type: 'boolean', required: false },
        { id: 'ewi_thermalCalculations', label: 'Thermal performance calculations', type: 'boolean', required: false },
        { id: 'ewi_condensationRisk', label: 'Condensation risk analysis', type: 'boolean', required: false },
        { id: 'ewi_ventilationReqs', label: 'Ventilation requirements and standard/bespoke drawing details', type: 'boolean', required: false },
        { id: 'ewi_systemComponents', label: 'The main components of the system including the fixing type/method, the insulation type and thickness, the reinforcing coat and type of reinforcement and the finish', type: 'boolean', required: false },
        { id: 'ewi_interfaces', label: 'The proposed details for the main interfaces', type: 'boolean', required: false },
        { id: 'ewi_practical', label: 'The installation to the retrofit design is practical and achievable given the particular EWI system chosen for the project and the specific dwelling construction, site conditions and other EEMs planned for the property', type: 'boolean', required: false },
        { id: 'ewi_buildingRegs', label: 'Will the installation result in non-compliance with the Building Regulations e.g. in relation to workmanship; materials; structural ability; fire safety', type: 'boolean', required: false },
        { id: 'ewi_moistureResistance', label: 'Will the installation provide resistance to moisture.', type: 'boolean', required: false },
        { id: 'ewi_thermalBridging', label: 'Will the installation result in avoidable thermal bridging', type: 'boolean', required: false },
        { id: 'ewi_combustionAppliances', label: 'Will the installation result in the unsafe operation of combustion appliances', type: 'boolean', required: false },
        { id: 'ewi_ventilationDucts', label: 'Will the installation compromise the functionality of existing ventilation ducts/systems', type: 'boolean', required: false },
        { id: 'ewi_services', label: 'Will the installation compromise the functionality and/or safety of existing services (gas, electric, water, telephone, etc.)', type: 'boolean', required: false },
        { id: 'ewi_supplierCompliance', label: 'Will the installation result in the proposed installation being non-compliant with any requirements of the EEM supplier or of the retrofit design.', type: 'boolean', required: false },
        { id: 'ewi_substrateCondition', label: 'Confirmation that the condition of the substrate is suitable for the works to commence and where all or any of the substrate does not fulfil the requirements for installation, preparation of proposals for adaptations to be made or additional preparation undertaken that will be necessary in order that works can commence.', type: 'boolean', required: false },
        { id: 'ewi_nonComplianceConfirmation', label: 'Confirmation all instances of potential non-compliance identified in the pre-installation building inspection shall be documented and referred to the Retrofit Coordinator for resolution. Any design adjustments, special adaptations and/or additional preparation requirements shall be confirmed as acceptable in writing, by the system supplier and/or the Retrofit Coordinator.', type: 'boolean', required: false },
        { id: 'ewi_designAmendment', label: 'Confirmation the retrofit design documentation shall be amended to include any specified changes to the installation, the installation method statement modified accordingly and the pre-installation building inspection records updated to provide documentary evidence that the intended modified installation will address all the issues identified in the pre-installation building inspection and meets the requirements of all parties.', type: 'boolean', required: false },
        { id: 'ewi_cavityWallConfirmation', label: 'Confirmation that the EWI is not being installed over walls of cavity construction that have had the cavities removed and not replaced, or have unfilled cavities. If they are to be so, that we have received confirmation from the Retrofit Designer that these cavities can be sealed in accordance with the specifications and construction details provided.', type: 'boolean', required: false },
        // CWI (7 fields)
        { id: 'cwi_combustionAppliances', label: 'Will the work result in the unsafe operation of combustion appliances?', type: 'boolean', required: false },
        { id: 'cwi_ventilationDucts', label: 'Will the work compromise the functionality of existing ventilation ducts/systems?', type: 'boolean', required: false },
        { id: 'cwi_services', label: 'Will the work compromise the functionality and/or safety of existing services? (gas, electric, water, telephone, etc.)', type: 'boolean', required: false },
        { id: 'cwi_supplierReqs', label: 'Will the proposed installation be non-compliant with any requirements stated by the supplier?', type: 'boolean', required: false },
        { id: 'cwi_buildingStructure', label: 'Is the type and condition of the building structure suitable for the works to commence?', type: 'boolean', required: false },
        { id: 'cwi_cavityExtent', label: 'What is the extent of the cavity to be filled?', type: 'text', required: false },
        { id: 'cwi_siteLayout', label: 'Will the site layout or conditions impair the execution of the works?', type: 'boolean', required: false },
        // RIR (2 fields)
        { id: 'rirInsulation_roofVentilation', label: 'The ventilation to spaces within the roof void and not included in the Room-in-Roof space, are checked and assessed to ensure that adequate ventilation is provided and maintained', type: 'boolean', required: false },
        { id: 'rirInsulation_thermalBridging', label: 'Confirmation that there is no requirement for thermal bridging to be addressed at the ridge or other connections with the main structure.', type: 'boolean', required: false },
      ],
      heating_system: [
        // Main Heating (1 field)
        { id: 'mainHeatingSource', label: 'Main Heating Source', type: 'dropdown', required: true },
        // Boiler Details (13 fields)
        { id: 'boilerManufacturer', label: 'Boiler Manufacturer', type: 'text', required: false },
        { id: 'boilerBrand', label: 'Boiler Brand', type: 'text', required: false },
        { id: 'modelQualifier', label: 'Model Qualifier', type: 'text', required: false },
        { id: 'boilerType', label: 'Boiler Type', type: 'dropdown', required: false },
        { id: 'fuelType', label: 'Fuel Type', type: 'dropdown', required: false },
        { id: 'sapWinterEfficiency', label: 'SAP Winter Efficiency (%)', type: 'text', required: false },
        { id: 'boilerAge', label: 'Boiler Age', type: 'text', required: false },
        { id: 'commissionYear', label: 'Commission Year', type: 'text', required: false },
        { id: 'serialNumber', label: 'Serial Number', type: 'text', required: false },
        { id: 'boilerLocation', label: 'Boiler Location', type: 'dropdown', required: false },
        { id: 'boilerPosition', label: 'Boiler Position', type: 'dropdown', required: false },
        { id: 'boilerPositionOther', label: 'Specify Other Position', type: 'text', required: false },
        { id: 'heatingSystemCondition', label: 'Heating System Condition', type: 'dropdown', required: false },
        // Boiler Photos (3 fields)
        { id: 'currentBoilerImage', label: 'Current Boiler', type: 'photo', required: false },
        { id: 'boilerDataBadgeImage', label: 'Boiler Data Badge', type: 'photo', required: false },
        { id: 'fluePipeImage', label: 'Flue Pipe', type: 'photo', required: false },
        // Additional Equipment (10 fields - boolean + photos + textarea)
        { id: 'hasCondensatePipe', label: 'Does the property have a condensate pipe?', type: 'boolean', required: false },
        { id: 'condensatePipeImage', label: 'Condensate Pipe Photos', type: 'photo', required: false },
        { id: 'hasOilLpgTank', label: 'Does the property have an oil/LPG tank?', type: 'boolean', required: false },
        { id: 'oilLpgTankImage', label: 'Oil/LPG Tank Photos', type: 'photo', required: false },
        { id: 'hasRenewables', label: 'Does the property have renewable energy systems?', type: 'boolean', required: false },
        { id: 'renewablesImage', label: 'Renewables Photos', type: 'photo', required: false },
        { id: 'hasCylinderWaterHeating', label: 'Does the property have a cylinder/water heating tank?', type: 'boolean', required: false },
        { id: 'cylinderWaterHeatingImage', label: 'Cylinder/Water Heating Photos', type: 'photo', required: false },
        { id: 'hasOtherBoilers', label: 'Are there other boilers in the property?', type: 'boolean', required: false },
        { id: 'otherBoilersDetails', label: 'Other Boilers Details', type: 'textarea', required: false },
        // Heating Controls (4 fields)
        { id: 'fullSetFunctioningControls', label: 'Does the system have a full set of functioning controls?', type: 'boolean', required: false },
        { id: 'preExistingHeatingControlsImages', label: 'Pre-existing Heating Controls Photos', type: 'photo', required: false },
        { id: 'systemHasBypassRadiator', label: 'Does the system have a bypass radiator?', type: 'boolean', required: false },
        { id: 'bypassRadiatorImage', label: 'Bypass Radiator Photos', type: 'photo', required: false },
      ],
      ventilation_condition: [
        // Room Counts (3 fields)
        { id: 'habitableRooms', label: 'Number of Habitable Rooms', type: 'text', required: false },
        { id: 'heatedHabitableRooms', label: 'Number of Heated Habitable Rooms', type: 'text', required: false },
        { id: 'totalRooms', label: 'Total Number of Rooms', type: 'text', required: false },
        // Windows (8 fields)
        { id: 'windowFramesCondition', label: 'Window Frames Condition', type: 'dropdown', required: false },
        { id: 'draughtProofingCondition', label: 'Draught Proofing Condition', type: 'dropdown', required: false },
        { id: 'windowFrameType', label: 'Window Frame Type', type: 'dropdown', required: false },
        { id: 'numberOfWindows', label: 'Number of Windows', type: 'text', required: false },
        { id: 'numberOfTrickleVents', label: 'Number of Trickle Vents', type: 'text', required: false },
        { id: 'windowGlazingType', label: 'Window Glazing Type', type: 'text', required: false },
        { id: 'windowGlazingMeasurement', label: 'Window Glazing Measurement', type: 'text', required: false },
        { id: 'windowGlazingThicknessImage', label: 'Window Glazing Thickness Photos', type: 'photo', required: false },
        // Doors (8 fields)
        { id: 'numberOfExternalDoors', label: 'Number of External Doors', type: 'text', required: false },
        { id: 'externalDoorsCondition', label: 'External Doors Condition', type: 'dropdown', required: false },
        { id: 'externalDoorsType', label: 'External Doors Type', type: 'dropdown', required: false },
        { id: 'draughtProofingOnExternalDoor', label: 'Draught Proofing on External Door', type: 'dropdown', required: false },
        { id: 'doorUndercutsPresent', label: 'Are door undercuts present?', type: 'boolean', required: false },
        { id: 'roomsWithNoDoorUndercuts', label: 'Rooms with No Door Undercuts', type: 'text', required: false },
        { id: 'externalDoorsImages', label: 'External Doors Photos', type: 'photo', required: false },
        { id: 'frontDoorOpenImage', label: 'Front Door (Open) Photos', type: 'photo', required: false },
        // Ventilation (4 fields)
        { id: 'numberOfWetRooms', label: 'Number of Wet Rooms', type: 'text', required: false },
        { id: 'numberOfVentilationFans', label: 'Number of Ventilation Fans', type: 'text', required: false },
        { id: 'roomsMissingVentilationFans', label: 'Rooms Missing Ventilation Fans', type: 'text', required: false },
        { id: 'ventilationTypePresent', label: 'Ventilation Types Present', type: 'text', required: false },
        // Roof (4 fields)
        { id: 'roofType', label: 'Roof Type', type: 'dropdown', required: false },
        { id: 'externalRoofCondition', label: 'External Roof Condition', type: 'dropdown', required: false },
        { id: 'visibleExternalRoofDefects', label: 'Are there visible external roof defects?', type: 'boolean', required: false },
        { id: 'roofDefectImages', label: 'Roof Defect Photos', type: 'photo', required: false },
        // Internal Walls (3 fields)
        { id: 'internalWallCondition', label: 'Internal Wall Condition', type: 'dropdown', required: false },
        { id: 'evidenceOfDamp', label: 'Is there evidence of damp?', type: 'boolean', required: false },
        { id: 'dampnessImages', label: 'Dampness Evidence Photos', type: 'photo', required: false },
        // Floor (3 fields)
        { id: 'floorConstructionType', label: 'Floor Construction Type', type: 'dropdown', required: false },
        { id: 'adequateSubFloorVentilation', label: 'Is there adequate sub-floor ventilation?', type: 'boolean', required: false },
        { id: 'evidenceOfFloorDampness', label: 'Is there evidence of floor dampness?', type: 'boolean', required: false },
        // External Walls (4 fields)
        { id: 'externalWallType', label: 'External Wall Type', type: 'dropdown', required: false },
        { id: 'externalWallCondition', label: 'External Wall Condition', type: 'dropdown', required: false },
        { id: 'additionalVisibleWallDefects', label: 'Are there additional visible wall defects?', type: 'boolean', required: false },
        { id: 'wallDefectImages', label: 'Wall Defect Photos', type: 'photo', required: false },
      ],
      main_property_measurements: [
        // Property Overview (2 fields)
        { id: 'numberOfStoreys', label: 'Number of Storeys', type: 'dropdown', required: false },
        { id: 'totalArea', label: 'Total Floor Area (m²)', type: 'text', required: false },
        // Cellar Details (5 fields)
        { id: 'hasCellar', label: 'Does the property have a cellar?', type: 'boolean', required: false },
        { id: 'cellarLength', label: 'Cellar Length (m)', type: 'text', required: false },
        { id: 'cellarWidth', label: 'Cellar Width (m)', type: 'text', required: false },
        { id: 'cellarArea', label: 'Cellar Area (m²)', type: 'text', required: false },
        { id: 'cellarPhotos', label: 'Cellar Photos', type: 'photo', required: false },
      ],
      signatures: [
        // Pre-Installation Assessment (12 fields)
        { id: 'asbestosChecksUndertaken', label: 'Have asbestos checks been undertaken in line with the retrofit design?', type: 'boolean', required: false },
        { id: 'servicesAffectedByInstallation', label: 'Are there any building services that may be affected by the installation?', type: 'boolean', required: false },
        { id: 'potentialRiskToServices', label: 'Is there any potential risk to the existing building services from the installation?', type: 'boolean', required: false },
        { id: 'installationInLineWithDesign', label: 'Will the installation be undertaken in line with the retrofit design?', type: 'boolean', required: false },
        { id: 'methodStatementSuitable', label: 'Is the method statement suitable for the installation?', type: 'boolean', required: false },
        { id: 'protectedSpeciesDisturbance', label: 'Is there any potential for disturbance to protected species?', type: 'boolean', required: false },
        { id: 'customerUnderstandsWork', label: 'Does the customer understand the work to be undertaken?', type: 'boolean', required: false },
        { id: 'customerAwareOfDisruption', label: 'Is the customer aware of potential disruption during installation?', type: 'boolean', required: false },
        { id: 'customerHasRequests', label: 'Does the customer have any specific requests or concerns?', type: 'boolean', required: false },
        { id: 'siteAccessArrangementsAdequate', label: 'Are site access arrangements adequate?', type: 'boolean', required: false },
        { id: 'otherInstallationInfo', label: 'Is there any other relevant installation information?', type: 'boolean', required: false },
        { id: 'potentialMoistureBuildUp', label: 'Is there potential for moisture build-up post-installation?', type: 'boolean', required: false },
        // Customer Declarations (8 fields)
        { id: 'customerStatement1', label: 'Customer Statement 1: Retrofit Assessor completion confirmation', type: 'text', required: false },
        { id: 'customerStatement1Date', label: 'Statement 1 Date', type: 'text', required: false },
        { id: 'customerStatement2', label: 'Customer Statement 2: Results via Retrofit Advisor', type: 'text', required: false },
        { id: 'customerStatement2Date', label: 'Statement 2 Date', type: 'text', required: false },
        { id: 'customerStatement3', label: 'Customer Statement 3: OFGEM scheme data sharing', type: 'text', required: false },
        { id: 'customerStatement3Date', label: 'Statement 3 Date', type: 'text', required: false },
        { id: 'customerStatement4', label: 'Customer Statement 4: Homeowner confirmation', type: 'text', required: false },
        { id: 'customerStatement4Date', label: 'Statement 4 Date', type: 'text', required: false },
        // Surveyor Sign Off (10 fields)
        { id: 'surveyorStatement1', label: 'Surveyor Statement 1: Collected all evidence per Retrofit Assessment scheme', type: 'text', required: false },
        { id: 'surveyorStatement1Date', label: 'Statement 1 Date', type: 'text', required: false },
        { id: 'surveyorStatement2', label: 'Surveyor Statement 2: GDPR rules per Eco Approach Ltd', type: 'text', required: false },
        { id: 'surveyorStatement2Date', label: 'Statement 2 Date', type: 'text', required: false },
        { id: 'surveyorStatement3', label: 'Surveyor Statement 3: Informed customer about PAS 2035', type: 'text', required: false },
        { id: 'surveyorStatement3Date', label: 'Statement 3 Date', type: 'text', required: false },
        { id: 'surveyorStatement4', label: 'Surveyor Statement 4: Customer property and GDPR care', type: 'text', required: false },
        { id: 'surveyorStatement4Date', label: 'Statement 4 Date', type: 'text', required: false },
        { id: 'surveyorStatement5', label: 'Surveyor Statement 5: Survey compliance check', type: 'text', required: false },
        { id: 'surveyorStatement5Date', label: 'Statement 5 Date', type: 'text', required: false },
        // Feedback (2 fields)
        { id: 'feedbackRating', label: 'Please rate the survey service (1-5)', type: 'dropdown', required: false },
        { id: 'feedbackComments', label: 'Any Feedback?', type: 'textarea', required: false },
      ],
      measure_specification: [
        // Suitable Measures (3 fields)
        { id: 'suitableMeasures', label: 'Select Suitable Measures', type: 'text', required: false },
        { id: 'wallInsulationType', label: 'Wall Insulation Type', type: 'dropdown', required: false },
        { id: 'wallInsulationElevationsCount', label: 'Number of Elevations', type: 'dropdown', required: false },
        
        // Loft Survey - Main Property (30+ fields)
        { id: 'mainPropertyRoofType', label: 'Main Property Roof Type', type: 'dropdown', required: true },
        { id: 'loftNeedsInsulating', label: 'Loft Needs Insulating', type: 'boolean', required: true },
        { id: 'loftHasExistingInsulation', label: 'Loft Has Existing Insulation', type: 'boolean', required: true },
        { id: 'mainLoftDepthOfInsulation', label: 'Current Depth of Insulation (mm)', type: 'number', required: false },
        { id: 'mainPropertyInsulationDepthEvidence', label: 'Insulation Depth Evidence Photos', type: 'photo', required: false },
        { id: 'accessToAllAreasOfLoft', label: 'Access to All Areas of Loft', type: 'boolean', required: false },
        { id: 'inaccessibleAreaM2', label: 'Inaccessible Area (m²)', type: 'number', required: false },
        { id: 'loftHatchAlreadyPresent', label: 'Loft Hatch Already Present', type: 'boolean', required: false },
        { id: 'isThereALoftLadder', label: 'Is There a Loft Ladder', type: 'boolean', required: false },
        { id: 'isTheLoftHatchAlreadyInsulated', label: 'Is Loft Hatch Insulated', type: 'boolean', required: false },
        { id: 'isTheLoftHatchDraughtProofed', label: 'Is Loft Hatch Draught Proofed', type: 'boolean', required: false },
        { id: 'loftHatchLengthMM', label: 'Loft Hatch Length (mm)', type: 'number', required: false },
        { id: 'loftHatchWidthMM', label: 'Loft Hatch Width (mm)', type: 'number', required: false },
        { id: 'mainLoftHatchEvidence', label: 'Loft Hatch Evidence Photos', type: 'photo', required: false },
        { id: 'mainPropertyLoftLength', label: 'Loft Length (m)', type: 'number', required: false },
        { id: 'mainPropertyLoftWidth', label: 'Loft Width (m)', type: 'number', required: false },
        { id: 'mainPropertyLoftHeight', label: 'Loft Height (m)', type: 'number', required: false },
        { id: 'mainPropertyRoofPitch', label: 'Roof Pitch (degrees)', type: 'number', required: false },
        { id: 'mainPropertyRoofPitchEvidence', label: 'Roof Pitch Evidence Photos', type: 'photo', required: false },
        { id: 'walkboardsRequired', label: 'Walkboards Required', type: 'boolean', required: false },
        { id: 'tenantsBelongingsToBeRemoved', label: 'Tenants Belongings To Be Removed', type: 'boolean', required: false },
        { id: 'explainedToOccupier', label: 'Explained To Occupier', type: 'boolean', required: false },
        { id: 'loftDownlightsPresent', label: 'Loft Downlights Present', type: 'boolean', required: false },
        { id: 'downlightCoversRequired', label: 'Downlight Covers Required', type: 'boolean', required: false },
        { id: 'downlightImage', label: 'Downlight Photos', type: 'photo', required: false },
        { id: 'coldWaterTankPresent', label: 'Cold Water Tank Present', type: 'boolean', required: false },
        { id: 'pipeworkPresent', label: 'Pipework Present', type: 'boolean', required: false },
        { id: 'coldWaterTankPipeworkNeedInsulation', label: 'Cold Water Tank/Pipework Need Insulation', type: 'boolean', required: false },
        { id: 'coldWaterTankPipeworkEvidence', label: 'Cold Water Tank/Pipework Evidence Photos', type: 'photo', required: false },
        { id: 'roofVentsPresent', label: 'Roof Vents Present', type: 'boolean', required: false },
        { id: 'roofVentsImage', label: 'Roof Vents Photos', type: 'photo', required: false },
        { id: 'verticalFlueInLoft', label: 'Vertical Flue In Loft', type: 'boolean', required: false },
        { id: 'highCurrentCablesPresent', label: 'High Current Cables Present', type: 'boolean', required: false },
        { id: 'mainLoftAreaImages', label: 'Loft Area Photos', type: 'photo', required: false },
        { id: 'numberOfExtensions', label: 'Number of Extensions', type: 'number', required: false },
        
        // Heating Controls (5 fields)
        { id: 'fullSetFunctioningControls', label: 'Full Set of Functioning Controls', type: 'boolean', required: false },
        { id: 'roomThermostat', label: 'Room Thermostat', type: 'boolean', required: false },
        { id: 'boilerProgrammer', label: 'Boiler Programmer', type: 'boolean', required: false },
        { id: 'smartThermostat', label: 'Smart Thermostat', type: 'boolean', required: false },
        { id: 'systemHasBypassRadiator', label: 'System Has Bypass Radiator', type: 'boolean', required: false },
        { id: 'preExistingHeatingControlsImages', label: 'Pre-Existing Heating Controls Photos', type: 'photo', required: false },
        { id: 'bypassRadiatorImage', label: 'Bypass Radiator Photos', type: 'photo', required: false },
        
        // Heating System - Existing Boiler (20 fields)
        { id: 'isBoilerPresent', label: 'Is Boiler Present', type: 'boolean', required: false },
        { id: 'boilerManufacturerName', label: 'Boiler Manufacturer Name', type: 'text', required: false },
        { id: 'boilerBrand', label: 'Boiler Brand', type: 'text', required: false },
        { id: 'modelQualifier', label: 'Model Qualifier', type: 'text', required: false },
        { id: 'boilerType', label: 'Boiler Type', type: 'dropdown', required: false },
        { id: 'fuelType', label: 'Fuel Type', type: 'dropdown', required: false },
        { id: 'sapWinterEfficiency', label: 'SAP Winter Efficiency', type: 'text', required: false },
        { id: 'ageOfBoiler', label: 'Age of Boiler', type: 'text', required: false },
        { id: 'yearOfCommissioning', label: 'Year of Commissioning', type: 'text', required: false },
        { id: 'serialNumber', label: 'Serial Number', type: 'text', required: false },
        { id: 'boilerLocation', label: 'Boiler Location', type: 'dropdown', required: false },
        { id: 'boilerPosition', label: 'Boiler Position', type: 'dropdown', required: false },
        { id: 'anyOtherBoilers', label: 'Any Other Boilers', type: 'boolean', required: false },
        { id: 'otherBoilersDetails', label: 'Other Boilers Details', type: 'text', required: false },
        { id: 'isCondensatePipe', label: 'Is Condensate Pipe Present', type: 'boolean', required: false },
        { id: 'condensatePipeTermination', label: 'Condensate Pipe Termination', type: 'dropdown', required: false },
        { id: 'imageOfCurrentBoiler', label: 'Current Boiler Photos', type: 'photo', required: false },
        { id: 'boilerDataBadge', label: 'Boiler Data Badge Photos', type: 'photo', required: false },
        { id: 'imageOfFluePipe', label: 'Flue Pipe Photos', type: 'photo', required: false },
        { id: 'imageOfCondensatePipe', label: 'Condensate Pipe Photos', type: 'photo', required: false },
        
        // Heating System - New System (20 fields)
        { id: 'newSystemHeat100Percent', label: 'Will New System Heat 100% of Property', type: 'boolean', required: false },
        { id: 'postHeatingSystem', label: 'Post Heating System', type: 'dropdown', required: false },
        { id: 'flueType', label: 'Flue Type', type: 'dropdown', required: false },
        { id: 'flueGuardRequired', label: 'Flue Guard Required', type: 'boolean', required: false },
        { id: 'flueFittingKitRequired', label: 'Flue Fitting Kit Required', type: 'boolean', required: false },
        { id: 'plumeKitRequired', label: 'Plume Kit Required', type: 'boolean', required: false },
        { id: 'frostProtectionRequired', label: 'Frost Protection Required', type: 'boolean', required: false },
        { id: 'condenseTermination', label: 'Condense Termination', type: 'dropdown', required: false },
        { id: 'gasMeterBoilerDistance', label: 'Gas Meter to Boiler Distance', type: 'number', required: false },
        { id: 'remedialWorksToBrickwork', label: 'Remedial Works To Brickwork', type: 'boolean', required: false },
        { id: 'newBoilerLocation', label: 'New Boiler Location', type: 'dropdown', required: false },
        { id: 'weatherCompensatorNeeded', label: 'Weather Compensator Needed', type: 'boolean', required: false },
        { id: 'loadCompensatorNeeded', label: 'Load Compensator Needed', type: 'boolean', required: false },
        { id: 'trvsRequired', label: 'TRVs Required', type: 'boolean', required: false },
        { id: 'lockShieldsRequired', label: 'Lock Shields Required', type: 'boolean', required: false },
        { id: 'coldWaterPressureAdequate', label: 'Cold Water Pressure Adequate', type: 'boolean', required: false },
        { id: 'mainsBoardLocation', label: 'Mains Board Location', type: 'text', required: false },
        { id: 'electricityMeterLocation', label: 'Electricity Meter Location', type: 'text', required: false },
        { id: 'stopTapLocation', label: 'Stop Tap Location', type: 'text', required: false },
        { id: 'stopTapImages', label: 'Stop Tap Photos', type: 'photo', required: false },
        { id: 'propertyEarthBondingPresent', label: 'Property Earth Bonding Present', type: 'boolean', required: false },
        { id: 'fuseSpurPresent', label: 'Fuse Spur Present', type: 'boolean', required: false },
        { id: 'pipeworkInUnheatedAreas', label: 'Pipework In Unheated Areas', type: 'boolean', required: false },
        
        // Room in Roof Survey (15 fields)
        { id: 'buildingType', label: 'Building Type', type: 'dropdown', required: false },
        { id: 'rirRoomsCount', label: 'Number of RIR Rooms', type: 'number', required: false },
        { id: 'rirFloorArea', label: 'RIR Floor Area (m²)', type: 'number', required: false },
        { id: 'rirResidualLoftArea', label: 'RIR Residual Loft Area (m²)', type: 'number', required: false },
        { id: 'roofPitchRIR', label: 'Roof Pitch (degrees)', type: 'number', required: false },
        { id: 'roofPitchEvidenceRIR', label: 'Roof Pitch Evidence Photos', type: 'photo', required: false },
        { id: 'internalSurfacesDamp', label: 'Internal Surfaces Damp', type: 'boolean', required: false },
        { id: 'needsInsulating', label: 'Needs Insulating', type: 'boolean', required: false },
        { id: 'downlightsPresent', label: 'Downlights Present', type: 'boolean', required: false },
        { id: 'downlightsEvidence', label: 'Downlights Evidence Photos', type: 'photo', required: false },
        { id: 'rirFreeOfStoredItems', label: 'RIR Free of Stored Items', type: 'boolean', required: false },
        { id: 'plumberRequired', label: 'Plumber Required', type: 'boolean', required: false },
        { id: 'electricianRequired', label: 'Electrician Required', type: 'boolean', required: false },
        { id: 'skirtingBoardsRequired', label: 'Skirting Boards Required', type: 'boolean', required: false },
        { id: 'skirtingBoardLength', label: 'Skirting Board Length (m)', type: 'number', required: false },
        { id: 'walkwayRequired', label: 'Walkway Required', type: 'boolean', required: false },
        { id: 'workingTanksPipesPresent', label: 'Working Tanks/Pipes Present', type: 'boolean', required: false },
        { id: 'fixedStairCase', label: 'Fixed Staircase', type: 'boolean', required: false },
        { id: 'dormerPresent', label: 'Dormer Present', type: 'boolean', required: false },
        { id: 'rirExteriorElevations', label: 'RIR Exterior Elevation Photos', type: 'photo', required: false },
        { id: 'rirInternalRoomPhotos', label: 'RIR Internal Room Photos', type: 'photo', required: false },
        
        // Stud Walls (10 fields)
        { id: 'studWallsPresent', label: 'Stud Walls Present', type: 'boolean', required: false },
        { id: 'studWallConstruction', label: 'Stud Wall Construction', type: 'dropdown', required: false },
        { id: 'studWallTotalArea', label: 'Stud Wall Total Area (m²)', type: 'number', required: false },
        { id: 'studWallElementInsulated', label: 'Stud Wall Element Insulated', type: 'boolean', required: false },
        { id: 'studWallRequiresInsulation', label: 'Stud Wall Requires Insulation', type: 'boolean', required: false },
        { id: 'studWallInstallationMethod', label: 'Stud Wall Installation Method', type: 'dropdown', required: false },
        { id: 'studWallInsulationType', label: 'Stud Wall Insulation Type', type: 'text', required: false },
        { id: 'studWall100PercentInstalled', label: 'Stud Wall 100% Installed', type: 'boolean', required: false },
        { id: 'studWallInsulationThickness', label: 'Stud Wall Insulation Thickness (mm)', type: 'number', required: false },
        { id: 'studWallImages', label: 'Stud Wall Photos', type: 'photo', required: false },
        
        // Sloping Ceilings (10 fields)
        { id: 'slopingCeilingsPresent', label: 'Sloping Ceilings Present', type: 'boolean', required: false },
        { id: 'slopingCeilingArea', label: 'Sloping Ceiling Area (m²)', type: 'number', required: false },
        { id: 'slopingCeilingElementInsulated', label: 'Sloping Ceiling Element Insulated', type: 'boolean', required: false },
        { id: 'slopingCeilingRequiresInsulation', label: 'Sloping Ceiling Requires Insulation', type: 'boolean', required: false },
        { id: 'slopingCeilingInstallationMethod', label: 'Sloping Ceiling Installation Method', type: 'dropdown', required: false },
        { id: 'slopingCeilingInsulationType', label: 'Sloping Ceiling Insulation Type', type: 'text', required: false },
        { id: 'slopingCeiling100PercentInstalled', label: 'Sloping Ceiling 100% Installed', type: 'boolean', required: false },
        { id: 'slopingCeilingInsulationThickness', label: 'Sloping Ceiling Insulation Thickness (mm)', type: 'number', required: false },
        { id: 'slopingCeilingImages', label: 'Sloping Ceiling Photos', type: 'photo', required: false },
        
        // Gable Walls (10 fields)
        { id: 'gableWallsPresent', label: 'Gable Walls Present', type: 'boolean', required: false },
        { id: 'gableWallConstruction', label: 'Gable Wall Construction', type: 'dropdown', required: false },
        { id: 'gableWallTotalArea', label: 'Gable Wall Total Area (m²)', type: 'number', required: false },
        { id: 'gableWallElementInsulated', label: 'Gable Wall Element Insulated', type: 'boolean', required: false },
        { id: 'gableRequiresInsulation', label: 'Gable Requires Insulation', type: 'boolean', required: false },
        { id: 'gableWallInstallationMethod', label: 'Gable Wall Installation Method', type: 'dropdown', required: false },
        { id: 'gableWallInsulationType', label: 'Gable Wall Insulation Type', type: 'text', required: false },
        { id: 'gableWall100PercentInstalled', label: 'Gable Wall 100% Installed', type: 'boolean', required: false },
        { id: 'gableWallInsulationThickness', label: 'Gable Wall Insulation Thickness (mm)', type: 'number', required: false },
        { id: 'gableWallImages', label: 'Gable Wall Photos', type: 'photo', required: false },
        
        // Party Walls (10 fields)
        { id: 'partyWallsPresent', label: 'Party Walls Present', type: 'boolean', required: false },
        { id: 'partyWallConstruction', label: 'Party Wall Construction', type: 'dropdown', required: false },
        { id: 'partyWallTotalArea', label: 'Party Wall Total Area (m²)', type: 'number', required: false },
        { id: 'partyWallElementInsulated', label: 'Party Wall Element Insulated', type: 'boolean', required: false },
        { id: 'partyWallRequiresInsulation', label: 'Party Wall Requires Insulation', type: 'boolean', required: false },
        { id: 'partyWallInstallationMethod', label: 'Party Wall Installation Method', type: 'dropdown', required: false },
        { id: 'partyWallInsulationType', label: 'Party Wall Insulation Type', type: 'text', required: false },
        { id: 'partyWall100PercentInstalled', label: 'Party Wall 100% Installed', type: 'boolean', required: false },
        { id: 'partyWallInsulationThickness', label: 'Party Wall Insulation Thickness (mm)', type: 'number', required: false },
        { id: 'partyWallEvidence', label: 'Party Wall Evidence Photos', type: 'photo', required: false },
        
        // Dormer Windows (10 fields)
        { id: 'dormerWindowsPresent', label: 'Dormer Windows Present', type: 'boolean', required: false },
        { id: 'dormerWindowCount', label: 'Dormer Window Count', type: 'number', required: false },
        { id: 'dormerCeilingTotalArea', label: 'Dormer Ceiling Total Area (m²)', type: 'number', required: false },
        { id: 'dormerRevealsTotalArea', label: 'Dormer Reveals Total Area (m²)', type: 'number', required: false },
        { id: 'dormerElementInsulated', label: 'Dormer Element Insulated', type: 'boolean', required: false },
        { id: 'dormerWindowsRequiresInsulation', label: 'Dormer Windows Requires Insulation', type: 'boolean', required: false },
        { id: 'dormerWindowInstallationMethod', label: 'Dormer Window Installation Method', type: 'dropdown', required: false },
        { id: 'dormerWindowInsulationType', label: 'Dormer Window Insulation Type', type: 'text', required: false },
        { id: 'dormerWindow100PercentInstalled', label: 'Dormer Window 100% Installed', type: 'boolean', required: false },
        { id: 'dormerWindowInsulationThickness', label: 'Dormer Window Insulation Thickness (mm)', type: 'number', required: false },
        { id: 'dormerWindowsImages', label: 'Dormer Windows Photos', type: 'photo', required: false },
        
        // Residual Areas (6 fields)
        { id: 'residualAreasPresent', label: 'Residual Areas Present', type: 'boolean', required: false },
        { id: 'residualLoftSpace', label: 'Residual Loft Space (m²)', type: 'number', required: false },
        { id: 'existingInsulationPresent', label: 'Existing Insulation Present', type: 'dropdown', required: false },
        { id: 'existingInsulationThickness', label: 'Existing Insulation Thickness (mm)', type: 'number', required: false },
        { id: 'residualAreasToBeInsulated', label: 'Residual Areas To Be Insulated (m²)', type: 'number', required: false },
        { id: 'residualAreaImages', label: 'Residual Area Photos', type: 'photo', required: false },
        
        // Flat Ceilings (8 fields)
        { id: 'flatCeilingPresent', label: 'Flat Ceiling Present', type: 'dropdown', required: false },
        { id: 'totalAreaFlatCeiling', label: 'Total Area Flat Ceiling (m²)', type: 'number', required: false },
        { id: 'flatCeilingElementInsulated', label: 'Flat Ceiling Element Insulated', type: 'boolean', required: false },
        { id: 'flatCeilingRequiresInsulation', label: 'Flat Ceiling Requires Insulation', type: 'boolean', required: false },
        { id: 'accessAboveFlatCeiling', label: 'Access Above Flat Ceiling', type: 'boolean', required: false },
        { id: 'flatCeilingInstallationMethod', label: 'Flat Ceiling Installation Method', type: 'dropdown', required: false },
        { id: 'flatCeilingInsulationType', label: 'Flat Ceiling Insulation Type', type: 'text', required: false },
        { id: 'flatCeiling100PercentInstalled', label: 'Flat Ceiling 100% Installed', type: 'boolean', required: false },
        { id: 'flatCeilingImage', label: 'Flat Ceiling Photos', type: 'photo', required: false },
      ],
    };
    
    console.log('[getBuiltInFieldDefinitions] Section:', sectionId, 'Field count:', (fieldDefinitions[sectionId] || []).length);
    return fieldDefinitions[sectionId] || [];
  };

  // Get ALL fields (built-in + custom) in order for a section
  const getAllFieldsForSection = (sectionId: string) => {
    // Check if this is a custom page
    const customPage = formTemplate.customPages?.find(p => p.id === sectionId);
    
    if (customPage) {
      // For custom pages, return all fields from the page
      return customPage.fields.map(f => ({ ...f, isCustom: true }));
    }
    
    // For built-in pages, return built-in + custom fields
    const builtInFields = getBuiltInFieldDefinitions(sectionId);
    const customFields = formTemplate.customFieldsForBuiltInPages?.[sectionId] || [];
    const fieldOrder = formTemplate.builtInFieldOrder?.[sectionId];
    
    // Create maps for quick lookup
    const builtInMap = new Map(builtInFields.map(f => [f.id, { ...f, isCustom: false }]));
    const customMap = new Map(customFields.map(f => [f.id, { ...f, isCustom: true }]));
    const allFieldsMap = new Map([...builtInMap, ...customMap]);
    
    // If we have a custom order, use it
    if (fieldOrder && fieldOrder.length > 0) {
      return fieldOrder
        .map(id => allFieldsMap.get(id))
        .filter((f): f is (typeof builtInFields[0] | FieldSchema) & { isCustom: boolean } => f !== undefined);
    }
    
    // Otherwise, return built-in fields followed by custom fields
    return [
      ...builtInFields.map(f => ({ ...f, isCustom: false })),
      ...customFields.map(f => ({ ...f, isCustom: true }))
    ];
  };

  // Extract existing section names from custom fields
  const getExistingSections = (sectionId: string): string[] => {
    const customFields = formTemplate.customFieldsForBuiltInPages?.[sectionId] || [];
    const sections = new Set<string>();
    customFields.forEach(field => {
      if (field.section) {
        sections.add(field.section);
      }
    });
    
    // Include the section from the field being edited (if it's a new section not yet saved)
    if (editingField && editingField.section) {
      sections.add(editingField.section);
    }
    
    return Array.from(sections).sort();
  };

  // Editor panel handlers
  const handleToggleFieldVisibility = (fieldId: string) => {
    const hiddenFields = formTemplate.hiddenBuiltInFields?.[currentSection] || [];
    const isCurrentlyHidden = hiddenFields.includes(fieldId);
    
    const newHiddenFields = isCurrentlyHidden
      ? hiddenFields.filter(id => id !== fieldId)
      : [...hiddenFields, fieldId];
    
    const newTemplate = {
      ...formTemplate,
      hiddenBuiltInFields: {
        ...(formTemplate.hiddenBuiltInFields || {}),
        [currentSection]: newHiddenFields
      }
    };
    
    setFormTemplate(newTemplate);
    saveTemplateToDatabase(newTemplate);
  };

  const handleReorderSections = (fromIndex: number, toIndex: number) => {
    console.log('[handleReorderSections] ======== START ========');
    console.log('[handleReorderSections] Moving section from', fromIndex, 'to', toIndex);
    const currentSectionOrder = formTemplate.sectionOrder?.[currentSection] || [];
    console.log('[handleReorderSections] Current section order:', currentSectionOrder);
    
    // ALWAYS rebuild complete section list to match what's visible in UI
    const builtInSectionIds = getBuiltInFieldSections(currentSection).map(s => s.id);
    console.log('[handleReorderSections] Built-in section IDs:', builtInSectionIds);
    
    const customSections = formTemplate.customFieldsForBuiltInPages?.[currentSection] || [];
    console.log('[handleReorderSections] Custom sections count:', customSections.length);
    
    const customSectionNames = [...new Set(customSections.filter(f => f.section).map(f => f.section!))];
    console.log('[handleReorderSections] Custom section names:', customSectionNames);
    
    // Check if there are any unsectioned custom fields
    const hasUnsectionedFields = customSections.some(f => !f.section);
    const unsectionedSection = hasUnsectionedFields ? ['custom_fields_unsectioned'] : [];
    console.log('[handleReorderSections] Has unsectioned fields:', hasUnsectionedFields, unsectionedSection);
    
    // Complete list of all sections that should be visible
    const allVisibleSections = [...builtInSectionIds, ...customSectionNames, ...unsectionedSection];
    console.log('[handleReorderSections] All visible sections:', allVisibleSections);
    
    // Start with current order if it exists, otherwise use visible sections
    let sectionOrder: string[] = [];
    if (currentSectionOrder.length > 0) {
      // Start with current order, filter out undefined/null AND invalid sections
      const filtered = currentSectionOrder.filter((id): id is string => id !== undefined && id !== null);
      console.log('[handleReorderSections] Filtered null/undefined:', filtered);
      
      // Remove sections that no longer exist (deleted custom sections)
      sectionOrder = filtered.filter(id => allVisibleSections.includes(id));
      console.log('[handleReorderSections] Filtered to only visible sections:', sectionOrder);
      console.log('[handleReorderSections] Removed invalid sections:', filtered.filter(id => !allVisibleSections.includes(id)));
      
      // Add any new sections that aren't in the order yet
      allVisibleSections.forEach(sectionId => {
        if (!sectionOrder.includes(sectionId)) {
          console.log('[handleReorderSections] Adding missing section to order:', sectionId);
          sectionOrder.push(sectionId);
        }
      });
    } else {
      sectionOrder = allVisibleSections;
      console.log('[handleReorderSections] Using all visible sections as initial order');
    }
    
    console.log('[handleReorderSections] Final complete order before swap:', sectionOrder);
    console.log('[handleReorderSections] Order length:', sectionOrder.length, 'fromIndex:', fromIndex, 'toIndex:', toIndex);
    
    // Validate indices to prevent out-of-bounds
    if (fromIndex < 0 || fromIndex >= sectionOrder.length || toIndex < 0 || toIndex >= sectionOrder.length) {
      console.error('[handleReorderSections] ❌ Invalid indices:', { fromIndex, toIndex, length: sectionOrder.length });
      return;
    }
    
    console.log('[handleReorderSections] ✓ Indices valid, performing swap...');
    // Perform the swap
    const [removed] = sectionOrder.splice(fromIndex, 1);
    console.log('[handleReorderSections] Removed from position', fromIndex, ':', removed);
    sectionOrder.splice(toIndex, 0, removed);
    console.log('[handleReorderSections] Inserted at position', toIndex, ':', removed);
    console.log('[handleReorderSections] After swap:', sectionOrder);
    
    // Update the form template
    const updatedTemplate: FormTemplate = {
      ...formTemplate,
      sectionOrder: {
        ...formTemplate.sectionOrder,
        [currentSection]: sectionOrder,
      },
    };
    
    console.log('[handleReorderSections] Updating template with new order');
    setFormTemplate(updatedTemplate);
    saveTemplateToDatabase(updatedTemplate);
  };

  const handleReorderFieldsInSection = (sectionId: string, fromIndex: number, toIndex: number) => {
    // This handles reordering fields within a custom section
    const customFields = formTemplate.customFieldsForBuiltInPages?.[currentSection] || [];
    const fieldsInSection = customFields.filter(f => f.section === sectionId);
    
    if (fieldsInSection.length === 0) return;
    
    // Get the field IDs for this section
    const fieldIds = fieldsInSection.map(f => f.id);
    
    // Swap the sectionOrder values
    const updatedFields = customFields.map(field => {
      if (field.section === sectionId) {
        const currentIndex = fieldsInSection.findIndex(f => f.id === field.id);
        let newOrder = field.sectionOrder || 0;
        
        if (currentIndex === fromIndex) {
          newOrder = fieldsInSection[toIndex].sectionOrder || toIndex;
        } else if (currentIndex === toIndex) {
          newOrder = fieldsInSection[fromIndex].sectionOrder || fromIndex;
        }
        
        return { ...field, sectionOrder: newOrder };
      }
      return field;
    });
    
    // Update the form template
    const updatedTemplate: FormTemplate = {
      ...formTemplate,
      customFieldsForBuiltInPages: {
        ...formTemplate.customFieldsForBuiltInPages,
        [currentSection]: updatedFields,
      },
    };
    
    setFormTemplate(updatedTemplate);
    saveFormTemplate(updatedTemplate);
  };

  const handleToggleSectionVisibility = (sectionName: string) => {
    const hiddenSections = formTemplate.hiddenSections?.[currentSection] || [];
    const isCurrentlyHidden = hiddenSections.includes(sectionName);
    
    const newHiddenSections = isCurrentlyHidden
      ? hiddenSections.filter(s => s !== sectionName)
      : [...hiddenSections, sectionName];
    
    const newTemplate = {
      ...formTemplate,
      hiddenSections: {
        ...(formTemplate.hiddenSections || {}),
        [currentSection]: newHiddenSections,
      },
      updatedAt: new Date().toISOString(),
    };
    
    setFormTemplate(newTemplate);
    saveTemplateToDatabase(newTemplate);
  };

  const handleAddCustomField = (position?: number) => {
    setEditingField(null);
    // Position is now relative to ALL fields, not just custom fields
    setInsertionPosition(position);
    setIsFieldModalOpen(true);
  };

  const handleEditCustomField = (field: FieldSchema) => {
    setEditingField(field);
    setIsFieldModalOpen(true);
  };

  const handleDeleteCustomField = (fieldId: string) => {
    // Check if this is a custom page
    const customPage = formTemplate.customPages?.find(p => p.id === currentSection);
    
    if (customPage) {
      // Delete field from custom page
      const updatedFields = customPage.fields.filter(f => f.id !== fieldId);
      const updatedCustomPages = formTemplate.customPages?.map(p =>
        p.id === currentSection ? { ...p, fields: updatedFields } : p
      ) || [];
      
      const newTemplate = {
        ...formTemplate,
        customPages: updatedCustomPages,
        updatedAt: new Date().toISOString()
      };
      
      setFormTemplate(newTemplate);
      saveTemplateToDatabase(newTemplate);
    } else {
      // Delete field from built-in page
      const customFields = formTemplate.customFieldsForBuiltInPages?.[currentSection] || [];
      const updatedFields = customFields.filter(f => f.id !== fieldId);
      
      const newTemplate = {
        ...formTemplate,
        customFieldsForBuiltInPages: {
          ...(formTemplate.customFieldsForBuiltInPages || {}),
          [currentSection]: updatedFields
        }
      };
      
      setFormTemplate(newTemplate);
      saveTemplateToDatabase(newTemplate);
    }
  };

  const handleSaveField = (field: FieldSchema) => {
    // Check if this is a custom page
    const customPage = formTemplate.customPages?.find(p => p.id === currentSection);
    
    if (customPage) {
      // Save field to custom page
      const existingIndex = customPage.fields.findIndex(f => f.id === field.id);
      let updatedFields: FieldSchema[];
      
      if (existingIndex >= 0) {
        // Editing existing field
        updatedFields = customPage.fields.map((f, i) => i === existingIndex ? field : f);
      } else {
        // Adding new field
        if (insertionPosition !== undefined && insertionPosition <= customPage.fields.length) {
          updatedFields = [...customPage.fields];
          updatedFields.splice(insertionPosition, 0, field);
        } else {
          updatedFields = [...customPage.fields, field];
        }
      }
      
      const updatedCustomPages = formTemplate.customPages?.map(p =>
        p.id === currentSection ? { ...p, fields: updatedFields } : p
      ) || [];
      
      const newTemplate = {
        ...formTemplate,
        customPages: updatedCustomPages,
        updatedAt: new Date().toISOString()
      };
      
      setFormTemplate(newTemplate);
      saveTemplateToDatabase(newTemplate);
    } else {
      // Save field to built-in page
      const customFields = formTemplate.customFieldsForBuiltInPages?.[currentSection] || [];
      const existingIndex = customFields.findIndex(f => f.id === field.id);
      
      let updatedFields: FieldSchema[];
      let updatedFieldOrder: string[];
      
      if (existingIndex >= 0) {
        // Editing existing field - update in place, don't change order
        updatedFields = customFields.map((f, i) => i === existingIndex ? field : f);
        updatedFieldOrder = formTemplate.builtInFieldOrder?.[currentSection] || [];
      } else {
        // Adding new field
        updatedFields = [...customFields, field];
        
        // Update the unified field order
        const currentOrder = formTemplate.builtInFieldOrder?.[currentSection] || [];
        const builtInFields = getBuiltInFieldDefinitions(currentSection);
        
        // If no order exists, create one with all built-in fields
        if (currentOrder.length === 0) {
          updatedFieldOrder = builtInFields.map(f => f.id);
        } else {
          updatedFieldOrder = [...currentOrder];
        }
        
        // Insert the new custom field at the specified position
        if (insertionPosition !== undefined && insertionPosition <= updatedFieldOrder.length) {
          updatedFieldOrder.splice(insertionPosition, 0, field.id);
        } else {
          updatedFieldOrder.push(field.id);
        }
      }
      
      const newTemplate = {
        ...formTemplate,
        customFieldsForBuiltInPages: {
          ...(formTemplate.customFieldsForBuiltInPages || {}),
          [currentSection]: updatedFields
        },
        builtInFieldOrder: {
          ...(formTemplate.builtInFieldOrder || {}),
          [currentSection]: updatedFieldOrder
        }
      };
      
      setFormTemplate(newTemplate);
      saveTemplateToDatabase(newTemplate);
    }
    
    setInsertionPosition(undefined); // Reset insertion position after saving
    setIsFieldModalOpen(false);
    setEditingField(null);
  };

  const handleReorderBuiltInFields = (fromIndex: number, toIndex: number) => {
    // Now reorders ALL fields (built-in + custom) together
    const allFields = getAllFieldsForSection(currentSection);
    const currentOrder = formTemplate.builtInFieldOrder?.[currentSection] || allFields.map((f: any) => f.id);
    
    const newOrder = [...currentOrder];
    const [movedItem] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedItem);
    
    const newTemplate = {
      ...formTemplate,
      builtInFieldOrder: {
        ...(formTemplate.builtInFieldOrder || {}),
        [currentSection]: newOrder
      },
      updatedAt: new Date().toISOString()
    };
    
    setFormTemplate(newTemplate);
    saveTemplateToDatabase(newTemplate);
  };

  const saveTemplateToDatabase = async (template: FormTemplate) => {
    const activeSurveyId = currentSurveyId || tempSurveyId;
    if (activeSurveyId) {
      const result = await saveFormTemplate(template, activeSurveyId);
      if (result.success) {
        console.log('✅ Template saved:', currentSection);
      } else {
        console.error('❌ Error saving template:', result.error);
      }
    }
  };

  // Generate UUID utility
  const generateUUID = () => uuidv4();

  // tempSurveyId is now generated synchronously above using useMemo
  // This ensures it's available immediately for photo uploads without race conditions

  // Expose photo cleanup utility on window object for debugging
  useEffect(() => {
    (window as any).cleanupSurveyPhotos = async (surveyId?: string) => {
      const idToClean = surveyId || currentSurveyId || actualSurveyId;
      if (!idToClean) {
        console.error('❌ No survey ID provided. Usage: cleanupSurveyPhotos("survey-id")');
        return;
      }
      
      console.log(`🧹 Starting photo cleanup for survey: ${idToClean}`);
      const result = await cleanupSurveyPhotos(idToClean);
      
      if (result.success) {
        const savedMB = ((result.beforeSize - result.afterSize) / 1024 / 1024).toFixed(2);
        console.log(`✅ Cleanup complete!`);
        console.log(`📊 Before: ${(result.beforeSize / 1024 / 1024).toFixed(2)} MB`);
        console.log(`📊 After: ${(result.afterSize / 1024 / 1024).toFixed(2)} MB`);
        console.log(`✨ Saved: ${savedMB} MB (${((1 - result.afterSize / result.beforeSize) * 100).toFixed(1)}% reduction)`);
        console.log(`📸 Photos processed: ${result.photosProcessed}`);
        console.log(`💡 Reload the page to see the updated survey!`);
      } else {
        console.error(`❌ Cleanup failed: ${result.error}`);
      }
      
      return result;
    };
    
    console.log(`💡 Photo cleanup utility loaded! Run: cleanupSurveyPhotos("${currentSurveyId || actualSurveyId || 'survey-id'}")`);
    
    return () => {
      delete (window as any).cleanupSurveyPhotos;
    };
  }, [currentSurveyId, actualSurveyId]);

  // Load existing survey data
  useEffect(() => {
    const loadSurveyData = async () => {
      if (!actualSurveyId) {
        // New survey - show address modal
        setShowAddressModal(true);
        return;
      }

      setIsDataLoading(true);
      try {
        console.log('[RetrofitSurveyLayout] Loading survey data for ID:', actualSurveyId);
        const loadedData = await loadRetrofitSurveyFromSupabase(actualSurveyId);
        console.log('[RetrofitSurveyLayout] Raw loaded data:', loadedData);
        setSurveyData(loadedData);
        setCurrentSurveyId(actualSurveyId);
        
        // Load survey-specific template
        console.log('[RetrofitSurveyLayout] Loading survey-specific template...');
        const { template: surveyTemplate, error: templateError } = await getOrCreateSurveyTemplate(actualSurveyId);
        if (surveyTemplate) {
          setFormTemplate(surveyTemplate);
          console.log('[RetrofitSurveyLayout] ✅ Survey template loaded:', surveyTemplate.id);
        } else {
          console.warn('[RetrofitSurveyLayout] ⚠️ Failed to load template, using default:', templateError);
        }
        
        // Set address flag if address exists
        const loadedAddress = getRetrofitSurveyAddress(loadedData);
        if (loadedAddress && loadedAddress.trim()) {
          setHasAddressBeenSet(true);
          setShowAddressModal(false); // Don't show modal for existing surveys with address
          console.log('[RetrofitSurveyLayout] Address found, hiding modal');
          
          // CRITICAL: Enable autosave flag for existing surveys with address
          // This ensures data updates in all sections will actually save
          // The actual autosave.enableAutosave() will be called in a separate effect below
          setAutosaveEnabled(true);
        } else {
          // Show address modal if loaded survey has no address
          setShowAddressModal(true);
          console.log('[RetrofitSurveyLayout] No address found, showing modal');
        }
        
        console.log('[RetrofitSurveyLayout] Loaded retrofit survey data:', retrofitSurveyId);
        
        // 🏢 Expose floor plan analysis to window for debugging
        exposeFloorPlanAnalysis(loadedData);
        
        // 🧹 Expose floor plan cleanup to window for manual cleaning
        exposeFloorPlanCleanup(loadedData, (updatedData) => {
          setSurveyData(updatedData);
          // Autosave will trigger automatically when surveyData changes
          console.log('✅ Floor plan data updated, autosave will trigger');
        });
      } catch (error) {
        console.error('[RetrofitSurveyLayout] Error loading survey data:', error);
        // Show address modal for failed loads too
        setShowAddressModal(true);
      } finally {
        setIsDataLoading(false);
      }
    };

    loadSurveyData();
  }, [actualSurveyId]);

  // Calculate if there are unsaved changes
  const calculateHasUnsavedChanges = useCallback((): boolean => {
    // Don't trigger autosave if autosave is not enabled yet
    if (!autosaveEnabled) {
      return false;
    }
    
    // For retrofit surveys, we consider there are unsaved changes if:
    // 1. An address has been set (indicating user started working)
    // 2. Any field in initial questionnaire has data beyond defaults
    const address = getRetrofitSurveyAddress(surveyData);
    const hasAddress = address && address.trim().length >= 3;
    
    if (!hasAddress && !hasAddressBeenSet) {
      return false; // No address set, no changes
    }
    
    // Check if any field has meaningful data beyond defaults
    const questionnaire = surveyData.initial_questionnaire;
    const hasFormData = questionnaire && (
      questionnaire.surveyorName?.trim() ||
      questionnaire.surveyDate?.trim() ||
      questionnaire.customerFullName?.trim() ||
      questionnaire.customerEmail?.trim() ||
      questionnaire.customerMobile?.trim() ||
      questionnaire.habitableRooms?.trim() ||
      questionnaire.heatedHabitableRooms?.trim() ||
      questionnaire.tenure?.trim() ||
      questionnaire.buildingAge?.trim() ||
      questionnaire.dwellingType?.trim() ||
      questionnaire.buildingOrientation?.trim() ||
      questionnaire.additionalNotes?.trim() ||
      questionnaire.hasMainsGas !== null ||
      questionnaire.hasOilLpgTank !== null ||
      questionnaire.hasRenewables !== null
    );
    
    // Return true if we have an address or meaningful form data
    return Boolean(hasAddressBeenSet || hasFormData);
  }, [surveyData, hasAddressBeenSet, autosaveEnabled]);

  // Direct save function that bypasses autosave debounce - for immediate saves after photo capture
  const saveImmediately = useCallback(async (): Promise<string> => {
    // Guard against overlapping saves to prevent database timeouts and lock contention
    if (saveInProgressRef.current) {
      console.log('[RetrofitSurveyLayout] ⏸️ Skipping immediate save - another save is already in progress');
      return currentSurveyId || tempSurveyId || '';
    }
    
    const startTime = performance.now();
    const existingId = currentSurveyId || tempSurveyId;
    const shouldForceInsert = existingId === tempSurveyId;
    
    // Mark save as in progress
    saveInProgressRef.current = true;
    
    console.log('[RetrofitSurveyLayout] 🚀 Immediate save STARTED:', { 
      existingId, 
      shouldForceInsert,
      timestamp: new Date().toISOString()
    });
    
    try {
      const savedId = await saveRetrofitSurveyToSupabase(
        generateRetrofitSurveyFilename(surveyData),
        surveyData,
        existingId || undefined,
        shouldForceInsert
      );
      
      const duration = performance.now() - startTime;
      
      if (tempSurveyId && !currentSurveyId) {
        setTempSurveyId(null);
      }
      setCurrentSurveyId(savedId);
      
      console.log('[RetrofitSurveyLayout] ✅ Immediate save COMPLETED:', { 
        savedId, 
        duration: `${duration.toFixed(2)}ms`,
        timestamp: new Date().toISOString()
      });
      
      // Clear the save in progress flag
      saveInProgressRef.current = false;
      
      return savedId;
    } catch (error) {
      const duration = performance.now() - startTime;
      console.error('[RetrofitSurveyLayout] ❌ Immediate save FAILED:', { 
        error, 
        duration: `${duration.toFixed(2)}ms`,
        timestamp: new Date().toISOString()
      });
      
      // Clear the save in progress flag even on error
      saveInProgressRef.current = false;
      
      throw error;
    }
  }, [surveyData, currentSurveyId, tempSurveyId]);

  // Autosave functionality
  const autosave = useAutosave(
    {
      id: currentSurveyId || tempSurveyId,
      data: surveyData,
      name: generateRetrofitSurveyFilename(surveyData),
      hasUnsavedChanges: calculateHasUnsavedChanges()
    },
    {
      saveFunction: async (name: string, dataToSave: RetrofitSurveyData, existingId?: string) => {
        // Guard against overlapping saves to prevent database timeouts and lock contention
        if (saveInProgressRef.current) {
          console.log('[RetrofitSurveyLayout] ⏸️ Skipping autosave - immediate save is in progress');
          return existingId || '';
        }
        
        const startTime = performance.now();
        const shouldForceInsert = existingId === tempSurveyId;
        
        // Mark save as in progress
        saveInProgressRef.current = true;
        
        // CRITICAL OPTIMIZATION: Strip base64 photo data before saving
        // Photos are already uploaded to CDN, we only need to save the CDN URLs
        const cleanedData = JSON.parse(JSON.stringify(dataToSave));
        const originalSize = JSON.stringify(dataToSave).length;
        
        // Strip base64String and dataUrl from photos that have been uploaded to CDN
        // KEEP base64 for photos that haven't been uploaded yet (offline access)
        const stripPhotoData = (photos: any): any => {
          if (!photos || typeof photos !== 'object') return photos;
          
          // Helper function to strip a single photo
          const stripSinglePhoto = (photo: any): any => {
            if (!photo || typeof photo !== 'object') return photo;
            if (photo.url && typeof photo.url === 'string' && photo.url.startsWith('http')) {
              const { base64String, dataUrl, ...rest } = photo;
              return rest; // Strip base64, keep URL
            }
            return photo; // Keep photo with base64 (not uploaded yet)
          };
          
          let strippedCount = 0;
          let keptCount = 0;
          const cleaned: any = {};
          
          for (const [key, value] of Object.entries(photos)) {
            // Handle RoomPhotos radiatorImages structure: [{ radiatorId, images: [] }]
            if (key === 'radiatorImages' && Array.isArray(value)) {
              cleaned[key] = value.map((radiator: any) => {
                if (radiator && radiator.images && Array.isArray(radiator.images)) {
                  return {
                    ...radiator,
                    images: radiator.images.map((photo: any) => stripSinglePhoto(photo))
                  };
                }
                return radiator;
              });
            }
            // Handle nested structure like { photos: [...] }
            else if (value && typeof value === 'object' && 'photos' in value && Array.isArray((value as any).photos)) {
              const photoArray = (value as any).photos;
              cleaned[key] = {
                ...value,
                photos: photoArray.map((photo: any) => stripSinglePhoto(photo))
              };
            }
            // Handle direct array of photos
            else if (Array.isArray(value)) {
              cleaned[key] = value.map((photo: any) => {
                if (photo && typeof photo === 'object' && ('dataUrl' in photo || 'url' in photo)) {
                  // This looks like a photo object
                  return stripSinglePhoto(photo);
                }
                return photo;
              });
            }
            // Handle single photo objects (doorUnderCutsImage, ventilationFansImage, etc)
            else if (value && typeof value === 'object' && ('dataUrl' in value || 'url' in value)) {
              cleaned[key] = stripSinglePhoto(value);
            }
            // Not a photo structure, keep as-is
            else {
              cleaned[key] = value;
            }
          }
          
          return cleaned;
        };
        
        // Clean photos in all sections
        console.log('[Autosave] Cleaning photos in sections:', {
          hasInitialPhotos: !!cleanedData.initial_questionnaire?.photos,
          hasFrontPhotos: !!cleanedData.property_front?.photos,
          hasRearPhotos: !!cleanedData.property_rear?.photos,
          hasLeftPhotos: !!cleanedData.property_left,
          hasRightPhotos: !!cleanedData.property_right,
          hasHeatingSystemPhotos: !!cleanedData.heating_system?.photos,
          hasRoomDetails: !!cleanedData.room_details,
          hasRoomDetailsRooms: !!cleanedData.room_details?.rooms,
          roomDetailsRoomsLength: cleanedData.room_details?.rooms?.length || 0,
          roomDetailsSample: cleanedData.room_details?.rooms?.[0] ? {
            id: cleanedData.room_details.rooms[0].id,
            hasPhotos: !!cleanedData.room_details.rooms[0].photos,
            photosKeys: cleanedData.room_details.rooms[0].photos ? Object.keys(cleanedData.room_details.rooms[0].photos) : []
          } : null
        });
        
        if (cleanedData.initial_questionnaire?.photos) {
          console.log('[Autosave] Processing initial_questionnaire.photos');
          cleanedData.initial_questionnaire.photos = stripPhotoData(cleanedData.initial_questionnaire.photos);
        }
        // property_front stores photos DIRECTLY, not in a photos sub-object
        if (cleanedData.property_front) {
          console.log('[Autosave] Processing property_front (direct photo arrays)');
          const photoKeys = ['frontElevationImage', 'wallTypeEvidenceImage', 'wallDefectImages', 
                            'roofTypeEvidenceImages', 'floorTypeEvidenceImages'];
          
          photoKeys.forEach(key => {
            if (Array.isArray(cleanedData.property_front[key])) {
              console.log(`[Autosave] Cleaning ${key}:`, cleanedData.property_front[key].length);
              cleanedData.property_front[key] = cleanedData.property_front[key].map((photo: any) => {
                if (photo && typeof photo === 'object') {
                  if (photo.url && typeof photo.url === 'string' && photo.url.startsWith('http')) {
                    console.log(`[Autosave] Stripping ${key} photo with URL`);
                    const { base64String, dataUrl, ...rest } = photo;
                    return rest;
                  }
                }
                return photo;
              });
            }
          });
        }
        
        // property_rear stores photos DIRECTLY, not in a photos sub-object
        if (cleanedData.property_rear) {
          console.log('[Autosave] Processing property_rear (direct photo arrays)');
          // Process photo arrays directly from property_rear
          const photoKeys = ['rearElevationImage', 'wallDefectImages', 'roofTypeEvidenceImages', 
                            'rearBackyardImages', 'gutteringDrainageImages', 'wallTypeEvidenceImage'];
          
          photoKeys.forEach(key => {
            if (Array.isArray(cleanedData.property_rear[key])) {
              console.log(`[Autosave] Cleaning ${key}:`, cleanedData.property_rear[key].length);
              cleanedData.property_rear[key] = cleanedData.property_rear[key].map((photo: any) => {
                if (photo && typeof photo === 'object') {
                  if (photo.url && typeof photo.url === 'string' && photo.url.startsWith('http')) {
                    console.log(`[Autosave] Stripping ${key} photo with URL`);
                    const { base64String, dataUrl, ...rest } = photo;
                    return rest;
                  }
                  console.log(`[Autosave] Keeping ${key} photo (no URL)`);
                }
                return photo;
              });
            }
          });
        }
        
        // property_left stores photos DIRECTLY, not in a photos sub-object
        if (cleanedData.property_left) {
          console.log('[Autosave] Processing property_left (direct photo arrays)');
          const photoKeys = ['leftElevationImage', 'wallDefectImages', 'leftSideImages', 'wallTypeEvidenceImage'];
          
          photoKeys.forEach(key => {
            if (Array.isArray(cleanedData.property_left[key])) {
              console.log(`[Autosave] Cleaning ${key}:`, cleanedData.property_left[key].length);
              cleanedData.property_left[key] = cleanedData.property_left[key].map((photo: any) => {
                if (photo && typeof photo === 'object') {
                  if (photo.url && typeof photo.url === 'string' && photo.url.startsWith('http')) {
                    const { base64String, dataUrl, ...rest } = photo;
                    return rest;
                  }
                }
                return photo;
              });
            }
          });
        }
        
        // property_right stores photos DIRECTLY, not in a photos sub-object
        if (cleanedData.property_right) {
          console.log('[Autosave] Processing property_right (direct photo arrays)');
          const photoKeys = ['rightElevationImage', 'wallDefectImages', 'rightSideImages', 'wallTypeEvidenceImage'];
          
          photoKeys.forEach(key => {
            if (Array.isArray(cleanedData.property_right[key])) {
              console.log(`[Autosave] Cleaning ${key}:`, cleanedData.property_right[key].length);
              cleanedData.property_right[key] = cleanedData.property_right[key].map((photo: any) => {
                if (photo && typeof photo === 'object') {
                  if (photo.url && typeof photo.url === 'string' && photo.url.startsWith('http')) {
                    const { base64String, dataUrl, ...rest } = photo;
                    return rest;
                  }
                }
                return photo;
              });
            }
          });
        }
        
        // Clean photos in heating_system
        if (cleanedData.heating_system?.photos) {
          console.log('[Autosave] Processing heating_system.photos');
          cleanedData.heating_system.photos = stripPhotoData(cleanedData.heating_system.photos);
        }
        
        // Clean photos in extensions
        if (cleanedData.extensions && Array.isArray(cleanedData.extensions)) {
          cleanedData.extensions = cleanedData.extensions.map((ext: any) => {
            if (ext.photos) {
              return { ...ext, photos: stripPhotoData(ext.photos) };
            }
            return ext;
          });
        }
        
        // Clean photos in rooms (floor plan linked data)
        if (cleanedData.floor_plan?.linkedFloorPlanData?.rooms) {
          cleanedData.floor_plan.linkedFloorPlanData.rooms = cleanedData.floor_plan.linkedFloorPlanData.rooms.map((room: any) => {
            if (room.photos) {
              return { ...room, photos: stripPhotoData(room.photos) };
            }
            return room;
          });
        }
        
        // Clean photos in room_details (room survey data)
        if (cleanedData.room_details?.rooms) {
          console.log('[Autosave] Processing room_details.rooms:', cleanedData.room_details.rooms.length);
          cleanedData.room_details.rooms = cleanedData.room_details.rooms.map((room: any) => {
            if (room.photos) {
              console.log(`[Autosave] Cleaning photos for room ${room.id}:`, room.name);
              return { ...room, photos: stripPhotoData(room.photos) };
            }
            return room;
          });
        }
        
        const cleanedSize = JSON.stringify(cleanedData).length;
        const reduction = ((originalSize - cleanedSize) / originalSize * 100).toFixed(1);
        
        console.log('[RetrofitSurveyLayout] 🔄 Autosave STARTED:', { 
          existingId, 
          tempSurveyId, 
          shouldForceInsert,
          originalSize: `${(originalSize / 1024 / 1024).toFixed(2)}MB`,
          cleanedSize: `${(cleanedSize / 1024 / 1024).toFixed(2)}MB`,
          reduction: `${reduction}%`,
          timestamp: new Date().toISOString()
        });
        
        try {
          const savedId = await saveRetrofitSurveyToSupabase(name, cleanedData, existingId, shouldForceInsert);
          const duration = performance.now() - startTime;
          
          console.log('[RetrofitSurveyLayout] ✅ Autosave COMPLETED:', { 
            savedId, 
            duration: `${duration.toFixed(2)}ms`,
            timestamp: new Date().toISOString()
          });
          
          // Clear the save in progress flag
          saveInProgressRef.current = false;
          
          return savedId;
        } catch (error) {
          const duration = performance.now() - startTime;
          console.error('[RetrofitSurveyLayout] ❌ Autosave FAILED:', { 
            error, 
            duration: `${duration.toFixed(2)}ms`,
            timestamp: new Date().toISOString()
          });
          
          // Clear the save in progress flag even on error
          saveInProgressRef.current = false;
          
          throw error;
        }
      },
      getDataToSave: () => surveyDataRef.current, // ✅ Read from ref for latest state
      getFileName: () => generateRetrofitSurveyFilename(surveyDataRef.current), // ✅ Read from ref
      validateBeforeSave: (context?: { address?: string }) => validateRetrofitSurveyData(surveyDataRef.current, context), // ✅ Read from ref
      onSaveSuccess: (savedId: string) => {
        if (tempSurveyId && !currentSurveyId) {
          setTempSurveyId(null);
        }
        setCurrentSurveyId(savedId);
      },
      onSaveError: (error: Error) => {
        console.error('[RetrofitSurveyLayout] Autosave error:', error);
      }
    },
    {
      enabled: false,
      intervalMs: 0, // Disabled - we use immediate saves on blur/unmount instead
      debounceMs: 1500, // Brief delay to batch rapid changes, then blur/unmount saves immediately
      maxRetries: 3
    }
  );

  // Enable autosave hook when autosaveEnabled flag is set and hook isn't enabled yet
  useEffect(() => {
    if (autosaveEnabled && !autosave.isAutosaveEnabled && !isDataLoading) {
      const address = getRetrofitSurveyAddress(surveyData);
      console.log('[RetrofitSurveyLayout] Enabling autosave hook:', { address, autosaveEnabled });
      if (address && address.trim().length >= 3) {
        setTimeout(() => {
          console.log('[RetrofitSurveyLayout] Calling autosave.enableAutosave for address:', address);
          autosave.enableAutosave({ address });
        }, 100);
      }
    }
  }, [autosaveEnabled, autosave, isDataLoading, surveyData]);

  // Handle address submission
  const handleAddressSubmit = useCallback(async (addressData: RetrofitAddressData) => {
    // Create combined address string for display
    // House number is combined with street name on same line
    const addressParts = [];
    if (addressData.buildingName) addressParts.push(addressData.buildingName);
    
    // Combine house number with street name
    const streetLine = addressData.houseNumberName 
      ? `${addressData.houseNumberName} ${addressData.streetAddress}`.trim()
      : addressData.streetAddress;
    addressParts.push(streetLine);
    
    addressParts.push(addressData.town);
    addressParts.push(addressData.postCode);
    
    const fullAddress = addressParts.filter(Boolean).join(', ');
    console.log('[RetrofitSurveyLayout] Address set:', fullAddress);
    
    // Get or generate a survey ID for the session
    const surveyId = currentSurveyId || tempSurveyId || generateUUID();
    if (!currentSurveyId && !tempSurveyId) {
      setTempSurveyId(surveyId);
    }
    
    // Update survey data with address - NO DATABASE OPERATION YET
    // Store building name, house number/name separately from street address
    const updatedData = {
      ...surveyData,
      initial_questionnaire: {
        ...surveyData.initial_questionnaire,
        buildingName: addressData.buildingName,
        houseNumberName: addressData.houseNumberName,
        streetAddress: addressData.streetAddress,
        town: addressData.town,
        postCode: addressData.postCode
      }
    };
    
    // Update local state only
    setSurveyData(updatedData);
    setShowAddressModal(false);
    setHasAddressBeenSet(true);
    
    // Enable autosave immediately - this will create the initial record when first triggered
    autosave.enableAutosave({ address: fullAddress });
    setAutosaveEnabled(true);
    console.log('[RetrofitSurveyLayout] Autosave enabled - will create initial record on first save');
  }, [autosave, surveyData, currentSurveyId, tempSurveyId, generateUUID]);

  // Handle skipping address
  const handleSkipAddress = useCallback(() => {
    setShowAddressModal(false);
    // Don't enable autosave without address
    console.log('[RetrofitSurveyLayout] Address skipped');
  }, []);

  // Helper to detect which sections changed
  const detectChangedSections = useCallback((prev: RetrofitSurveyData, updated: RetrofitSurveyData): string[] => {
    const changed: string[] = [];
    const sections = [
      'initial_questionnaire',
      'occupancy_assessment',
      'ventilation_condition',
      'heating_system',
      'site_assessment',
      'measure_specification',
      'property_front',
      'property_rear',
      'property_right',
      'property_left',
      'room_details',
      'meters',
      'main_property_measurements',
      'extensions',
      'floor_plan'
    ] as const;
    
    sections.forEach(section => {
      const prevSection = JSON.stringify((prev as any)[section]);
      const updatedSection = JSON.stringify((updated as any)[section]);
      if (prevSection !== updatedSection) {
        changed.push(section);
      }
    });
    
    return changed;
  }, []);

  // Partial update for individual field changes
  const partialUpdateSurveyData = useCallback(async (sectionId: string, updates: Partial<RetrofitSurveyData>) => {
    const surveyId = currentSurveyId || tempSurveyId;
    
    if (!surveyId) {
      console.log('[RetrofitSurveyLayout] ⏭️ No survey ID, skipping partial update');
      return;
    }
    
    try {
      console.log(`[RetrofitSurveyLayout] 🔄 Performing partial update for section: ${sectionId}`);
      await partialUpdateRetrofitSurvey(surveyId, updates, sectionId);
      console.log(`[RetrofitSurveyLayout] ✅ Partial update successful for: ${sectionId}`);
    } catch (error) {
      console.error(`[RetrofitSurveyLayout] ❌ Partial update failed for: ${sectionId}`, error);
      throw error;
    }
  }, [currentSurveyId, tempSurveyId]);

  // Update survey data helper with autosave trigger
  const updateSurveyData = useCallback((updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => {
    setSurveyData(prev => {
      const updated = updater(prev);
      
      // OPTIMIZATION: If sectionId is provided, skip expensive full-survey stringify
      // Just check if the specific section changed
      let hasAnyChanges = false;
      let changedSections: string[] = [];
      
      if (sectionId) {
        // Fast path: only stringify the specific section
        const prevSection = JSON.stringify((prev as any)[sectionId]);
        const updatedSection = JSON.stringify((updated as any)[sectionId]);
        hasAnyChanges = prevSection !== updatedSection;
        
        // DEBUG: Log comparison details
        console.log(`[RetrofitSurveyLayout] 🔍 Change detection for "${sectionId}":`, {
          prevSize: (prevSection.length / 1024).toFixed(2) + ' KB',
          updatedSize: (updatedSection.length / 1024).toFixed(2) + ' KB',
          sizeDiff: ((prevSection.length - updatedSection.length) / 1024).toFixed(2) + ' KB',
          hasChanges: hasAnyChanges,
          prevIsUpdated: prev === updated,
          sectionRefsSame: (prev as any)[sectionId] === (updated as any)[sectionId]
        });
        
        if (hasAnyChanges) {
          changedSections = [sectionId];
        }
      } else {
        // Slow path: full survey comparison (only when no sectionId provided)
        const prevData = JSON.stringify(prev);
        const updatedData = JSON.stringify(updated);
        hasAnyChanges = prevData !== updatedData;
        if (hasAnyChanges) {
          changedSections = detectChangedSections(prev, updated);
        }
      }
      
      if (!hasAnyChanges) {
        console.log('[RetrofitSurveyLayout] ⏭️ No data changes detected, skipping autosave');
        return prev; // Return previous state to prevent re-render
      }
      
      // Check if ANY floor plan data changed - if so, this update is from RetrofitFloorPlan
      const hasFloorPlanChanges = changedSections.includes('floor_plan');
      
      console.log('[RetrofitSurveyLayout] 🔄 Survey data updated:', {
        changedSections,
        sectionId,
        hasFloorPlanChanges,
        autosaveEnabled,
        isAutosaveEnabled: autosave.isAutosaveEnabled,
        optimizedPath: !!sectionId
      });
      
      // If we have a survey ID and a specific section changed, do a partial update immediately
      if ((currentSurveyId || tempSurveyId) && sectionId && changedSections.length === 1) {
        // Perform partial update for single section changes
        const sectionData: Partial<RetrofitSurveyData> = {};
        (sectionData as any)[sectionId] = (updated as any)[sectionId];
        
        // Fire partial update and sync autosave state on success
        partialUpdateSurveyData(sectionId, sectionData)
          .then(() => {
            // CRITICAL: Mark autosave as synced to avoid redundant full save
            autosave.syncWithCurrentData();
            console.log('[RetrofitSurveyLayout] ✅ Partial update completed and autosave synced');
          })
          .catch(err => {
            console.error('[RetrofitSurveyLayout] ❌ Partial update error:', err);
            // On error, let autosave retry with full save
          });
        
        console.log('[RetrofitSurveyLayout] ⚡ Triggered partial update for:', sectionId);
      } else if (!hasFloorPlanChanges) {
        // For multiple section changes or no sectionId, use full autosave
        console.log('[RetrofitSurveyLayout] ✅ Data changed - autosave will trigger automatically');
      } else {
        console.log('[RetrofitSurveyLayout] ⏭️ Floor plan changes detected - skipping autosave trigger');
      }
      
      return updated;
    });
  }, [autosaveEnabled, autosave, hasAddressBeenSet, currentSurveyId, tempSurveyId, detectChangedSections, partialUpdateSurveyData]);

  // Centralized photo migration - runs once on survey load
  const migrationInfo = useCentralizedPhotoMigration(
    surveyData,
    currentSurveyId || tempSurveyId || actualSurveyId || undefined,
    updateSurveyData,
    navigator.onLine
  );

  // Log migration progress
  useEffect(() => {
    if (migrationInfo.stats.totalPhotos > 0) {
      console.log('[RetrofitSurveyLayout] 📊 Photo Migration Status:', {
        total: migrationInfo.stats.totalPhotos,
        needingMigration: migrationInfo.stats.photosNeedingMigration,
        migrated: migrationInfo.stats.photosMigrated,
        withCDN: migrationInfo.stats.photosWithCDN,
        errors: migrationInfo.stats.errors,
        isComplete: migrationInfo.isComplete
      });
    }
  }, [migrationInfo.stats, migrationInfo.isComplete]);

  // 🏠 ADDRESS CHANGE MONITOR: Update survey name when address changes
  const lastAddressRef = useRef<string>('');
  useEffect(() => {
    const currentAddress = getRetrofitSurveyAddress(surveyData);
    
    // Only update if we have a valid survey ID and the address actually changed
    if ((currentSurveyId || tempSurveyId) && currentAddress && currentAddress.trim().length >= 3) {
      if (lastAddressRef.current && lastAddressRef.current !== currentAddress) {
        console.log('[RetrofitSurveyLayout] 🏠 Address changed - updating survey name:', {
          oldAddress: lastAddressRef.current,
          newAddress: currentAddress
        });
        
        // Generate new filename based on new address
        const newFilename = generateRetrofitSurveyFilename(surveyData);
        
        // Update survey data with new name
        setSurveyData(prev => ({
          ...prev,
          name: newFilename
        }));
        
        // Trigger autosave to persist the new name
        autosave.markAsChanged();
      }
      
      lastAddressRef.current = currentAddress;
    }
  }, [surveyData.initial_questionnaire.houseNumberName, surveyData.initial_questionnaire.buildingName, surveyData.initial_questionnaire.streetAddress, surveyData.initial_questionnaire.town, surveyData.initial_questionnaire.postCode, currentSurveyId, tempSurveyId, autosave]);

  const handleBuildFormChange = (newBuildForm: string, newExposedSide: 'left' | 'right' | 'both' | 'none' | '') => {
    console.log('[RetrofitSurveyLayout] handleBuildFormChange called:', { newBuildForm, newExposedSide });
    setBuildForm(newBuildForm);
    setExposedSide(newExposedSide);
    console.log('[RetrofitSurveyLayout] State updated - buildForm:', newBuildForm, 'exposedSide:', newExposedSide);
    
    // Update survey data with partial save
    updateSurveyData(prev => ({
      ...prev,
      property_front: {
        ...prev.property_front,
        buildForm: newBuildForm,
        exposedSide: newExposedSide
      }
    }), 'property_front');
  };

  // ==================== CUSTOM PAGE MANAGEMENT ====================
  
  // Helper to get Lucide icon component from icon name
  const getIconComponent = (iconName?: string): LucideIcon => {
    if (!iconName) return LucideIcons.FileText;
    const Icon = (LucideIcons as any)[iconName];
    return Icon || LucideIcons.FileText;
  };

  // Create a new custom page
  const handleCreatePage = (label: string, iconName?: string) => {
    const customPages = formTemplate.customPages || [];
    const newPageId = `custom_page_${Date.now()}`;
    
    const newPage: CustomPage = {
      id: newPageId,
      label,
      icon: iconName,
      fields: [],
      createdAt: new Date().toISOString(),
      order: customPages.length
    };

    const updatedTemplate: FormTemplate = {
      ...formTemplate,
      customPages: [...customPages, newPage],
      updatedAt: new Date().toISOString()
    };

    setFormTemplate(updatedTemplate);
    saveTemplateToDatabase(updatedTemplate);
    
    // Navigate to the new page
    setCurrentSection(newPageId);
  };

  // Delete a custom page
  const handleDeletePage = (pageId: string) => {
    if (!formTemplate.customPages) return;
    
    const updatedPages = formTemplate.customPages.filter(p => p.id !== pageId);
    const updatedPageOrder = formTemplate.pageOrder?.filter(id => id !== pageId);
    
    const updatedTemplate: FormTemplate = {
      ...formTemplate,
      customPages: updatedPages,
      pageOrder: updatedPageOrder,
      updatedAt: new Date().toISOString()
    };

    setFormTemplate(updatedTemplate);
    saveTemplateToDatabase(updatedTemplate);
    
    // Navigate away if we're on the deleted page
    if (currentSection === pageId) {
      setCurrentSection('initial_questionnaire');
    }
  };

  // Page reordering handlers with arrow buttons
  const handleMovePageLeft = (index: number) => {
    if (index === 0) return; // Can't move first page left
    
    // Get current page order from visibleSections
    const currentOrder = visibleSections.map(s => s.id);
    
    // Swap with previous page
    const newOrder = [...currentOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];

    // Save the new order
    const updatedTemplate: FormTemplate = {
      ...formTemplate,
      pageOrder: newOrder,
      updatedAt: new Date().toISOString()
    };

    setFormTemplate(updatedTemplate);
    saveTemplateToDatabase(updatedTemplate);
  };

  const handleMovePageRight = (index: number) => {
    if (index === visibleSections.length - 1) return; // Can't move last page right
    
    // Get current page order from visibleSections
    const currentOrder = visibleSections.map(s => s.id);
    
    // Swap with next page
    const newOrder = [...currentOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];

    // Save the new order
    const updatedTemplate: FormTemplate = {
      ...formTemplate,
      pageOrder: newOrder,
      updatedAt: new Date().toISOString()
    };

    setFormTemplate(updatedTemplate);
    saveTemplateToDatabase(updatedTemplate);
  };

  const handleEnterReorderMode = () => {
    setIsEditMode(false); // Close edit panel
    setIsReorderMode(true);
  };

  const handleExitReorderMode = () => {
    setIsReorderMode(false);
  };

  // ==================== END CUSTOM PAGE MANAGEMENT ====================

  const visibleSections = useMemo(() => {
    console.log('[RetrofitSurveyLayout] Computing visibleSections with:', { buildForm, exposedSide });
    
    // Filter built-in sections based on property configuration
    const filteredBuiltInSections = allSections.filter(section => {
      if (section.id === 'property_rear') {
        // Only hide rear for enclosed-mid-terrace (mid-terrace should still show rear)
        const visible = buildForm !== 'enclosed-mid-terrace';
        console.log(`[RetrofitSurveyLayout] property_rear visibility: ${visible}`);
        return visible;
      }
      if (section.id === 'property_right') {
        const visible = exposedSide === 'right' || exposedSide === 'both';
        console.log(`[RetrofitSurveyLayout] property_right visibility: ${visible} (exposedSide: ${exposedSide})`);
        return visible;
      }
      if (section.id === 'property_left') {
        const visible = exposedSide === 'left' || exposedSide === 'both';
        console.log(`[RetrofitSurveyLayout] property_left visibility: ${visible} (exposedSide: ${exposedSide})`);
        return visible;
      }
      return true;
    });

    // Convert custom pages to SurveySection format
    const customSections: SurveySection[] = (formTemplate.customPages || []).map(page => ({
      id: page.id,
      label: page.label,
      icon: getIconComponent(page.icon),
      isCustom: true
    }));

    // Combine all sections
    const allAvailableSections = [...filteredBuiltInSections, ...customSections];
    
    console.log('[RetrofitSurveyLayout] After filtering:', {
      filteredBuiltInSectionsCount: filteredBuiltInSections.length,
      filteredBuiltInSections: filteredBuiltInSections.map(s => s.id),
      hasPropertyRight: filteredBuiltInSections.some(s => s.id === 'property_right'),
      hasPropertyLeft: filteredBuiltInSections.some(s => s.id === 'property_left'),
      allAvailableSectionsCount: allAvailableSections.length
    });

    // Apply custom page order if it exists
    if (formTemplate.pageOrder && formTemplate.pageOrder.length > 0) {
      console.log('[RetrofitSurveyLayout] Applying custom pageOrder:', formTemplate.pageOrder);
      const orderedSections: SurveySection[] = [];
      const sectionMap = new Map(allAvailableSections.map(s => [s.id, s]));

      // Add sections in the specified order
      formTemplate.pageOrder.forEach(pageId => {
        const section = sectionMap.get(pageId);
        if (section) {
          orderedSections.push(section);
          sectionMap.delete(pageId);
        }
      });

      // Add any remaining sections that weren't in the order (newly added built-in sections)
      // IMPORTANT: Always add property_left/property_right if they passed the buildForm filter
      // This ensures they appear even if pageOrder doesn't include them yet
      sectionMap.forEach(section => {
        orderedSections.push(section);
      });
      
      console.log('[RetrofitSurveyLayout] Remaining sections added:', {
        remainingSections: Array.from(sectionMap.values()).map(s => s.id),
        hasPropertyLeft: Array.from(sectionMap.values()).some(s => s.id === 'property_left'),
        hasPropertyRight: Array.from(sectionMap.values()).some(s => s.id === 'property_right')
      });

      console.log('[RetrofitSurveyLayout] After applying pageOrder:', {
        orderedSectionsCount: orderedSections.length,
        orderedSections: orderedSections.map(s => s.id),
        hasPropertyRight: orderedSections.some(s => s.id === 'property_right'),
        hasPropertyLeft: orderedSections.some(s => s.id === 'property_left')
      });
      return orderedSections;
    }

    // No custom order - return all sections in default order
    console.log('[RetrofitSurveyLayout] No custom pageOrder, returning all available sections');
    return allAvailableSections;
  }, [buildForm, exposedSide, formTemplate.customPages, formTemplate.pageOrder]);
  
  // Log final visibleSections result
  console.log('[RetrofitSurveyLayout] ✅ FINAL visibleSections:', {
    count: visibleSections.length,
    sections: visibleSections.map(s => s.id),
    hasPropertyLeft: visibleSections.some(s => s.id === 'property_left'),
    hasPropertyRight: visibleSections.some(s => s.id === 'property_right'),
    propertyLeftIndex: visibleSections.findIndex(s => s.id === 'property_left'),
    propertyRightIndex: visibleSections.findIndex(s => s.id === 'property_right')
  });

  const activeIndex = visibleSections.findIndex(s => s.id === currentSection);

  const handleNavigateNext = () => {
    if (activeIndex < visibleSections.length - 1) {
      setCurrentSection(visibleSections[activeIndex + 1].id);
    }
  };

  const handleNavigateBack = () => {
    if (activeIndex > 0) {
      setCurrentSection(visibleSections[activeIndex - 1].id);
    }
  };

  // Helper to render custom fields for built-in sections
  const renderCustomFieldsForBuiltInSection = (sectionId: string) => {
    const customFields = formTemplate.customFieldsForBuiltInPages?.[sectionId] || [];
    const activeSurveyId = currentSurveyId || tempSurveyId;
    
    // Create a temporary page for these custom fields
    const customPage: typeof formTemplate.pages[0] = {
      id: `${sectionId}_custom`,
      title: 'Custom Fields',
      description: 'Additional custom fields for this section',
      showInPDF: true,
      pdfOrder: 999,
      fields: customFields
    };
    
    // Create temporary template with just this page
    const tempTemplate: FormTemplate = {
      ...formTemplate,
      pages: [customPage]
    };
    
    return (
      <div className="mt-6 border-t-2 border-gray-200 pt-6">
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {isEditMode ? 'Custom Fields (Click + Add Field to add more)' : 'Custom Fields'}
              </h3>
              <p className="text-xs text-blue-700 mt-1">
                These are additional custom fields you've added to this section.
              </p>
            </div>
          </div>
        </div>
        
        <DynamicFormRenderer
          template={tempTemplate}
          pageId={`${sectionId}_custom`}
          formData={surveyData.custom_fields?.[sectionId] || {}}
          onDataChange={(fieldId: string, value: any) => {
            updateSurveyData((prev) => ({
              ...prev,
              custom_fields: {
                ...(prev.custom_fields || {}),
                [sectionId]: {
                  ...((prev.custom_fields || {})[sectionId] || {}),
                  [fieldId]: value
                }
              }
            }));
          }}
          editMode={isEditMode}
          onTemplateChange={(updatedTemplate: FormTemplate) => {
            // Extract the custom fields from the updated template
            const updatedFields = updatedTemplate.pages[0].fields;
            
            // Update the main template
            const newTemplate = {
              ...formTemplate,
              customFieldsForBuiltInPages: {
                ...(formTemplate.customFieldsForBuiltInPages || {}),
                [sectionId]: updatedFields
              }
            };
            
            setFormTemplate(newTemplate);
            
            // Auto-save template changes
            if (activeSurveyId) {
              saveFormTemplate(newTemplate, activeSurveyId).then((result) => {
                if (result.success) {
                  console.log('✅ Custom fields saved for section:', sectionId);
                } else {
                  console.error('❌ Error saving custom fields:', result.error);
                }
              });
            }
          }}
        />
      </div>
    );
  };

  const renderSection = () => {
    // IMPORTANT: Check built-in sections FIRST to prevent custom pages from overriding them
    switch (currentSection) {
      case 'initial_questionnaire':
        return (
          <>
            <RetrofitSurveyForm
              onNavigate={onNavigate}
              onNavigateNext={handleNavigateNext}
              floorPlanId={floorPlanId}
              surveyData={surveyData}
              surveyId={currentSurveyId || tempSurveyId || actualSurveyId || undefined}
              onDataUpdate={updateSurveyData}
              onBatchUploadStart={() => {}}
              onBatchUploadEnd={() => {}}
              onManualSave={saveImmediately}
              onSaveImmediately={saveImmediately}
              onGalleryStateChange={setIsGalleryOpen}
              hiddenFields={formTemplate.hiddenBuiltInFields?.['initial_questionnaire'] || []}
              hiddenSections={formTemplate.hiddenSections?.['initial_questionnaire'] || []}
              fieldOrder={formTemplate.builtInFieldOrder?.['initial_questionnaire']}
              customFields={formTemplate.customFieldsForBuiltInPages?.['initial_questionnaire'] || []}
              sectionOrder={formTemplate.sectionOrder?.['initial_questionnaire']}
            />
          </>
        );
      case 'occupancy_assessment':
        return (
          <OccupancyAssessmentFormSchemaVersion
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            customFields={formTemplate.customFieldsForBuiltInPages?.occupancy_assessment || []}
            hiddenFields={formTemplate.hiddenBuiltInFields?.occupancy_assessment || []}
            hiddenSections={formTemplate.hiddenSections?.occupancy_assessment || []}
            sectionOrder={formTemplate.sectionOrder?.occupancy_assessment || []}
            builtInFieldOrder={formTemplate.builtInFieldOrder?.occupancy_assessment || []}
            onNavigateToPrevious={handleNavigateBack}
            onNavigateToNext={handleNavigateNext}
          />
        );
      case 'ventilation_condition':
        return (
          <VentilationConditionSurveyForm
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            surveyId={currentSurveyId || tempSurveyId || surveyData?.id || actualSurveyId || undefined}
            floorPlanId={surveyData?.floor_plan?.floorPlanId}
            onNavigateBack={handleNavigateBack}
            onNavigateNext={handleNavigateNext}
            onGalleryStateChange={setIsGalleryOpen}
          />
        );
      case 'heating_system':
        return (
          <HeatingSystemFormSchemaVersion
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            customFields={formTemplate.customFieldsForBuiltInPages?.heating_system || []}
            hiddenFields={formTemplate.hiddenBuiltInFields?.heating_system || []}
            hiddenSections={formTemplate.hiddenSections?.heating_system || []}
            sectionOrder={formTemplate.sectionOrder?.heating_system || []}
            builtInFieldOrder={formTemplate.builtInFieldOrder?.heating_system || []}
            onNavigateToPrevious={handleNavigateBack}
            onNavigateToNext={handleNavigateNext}
          />
        );
      case 'site_assessment':
        return (
          <SiteAssessmentFormSchemaVersion
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            customFields={formTemplate.customFieldsForBuiltInPages?.site_assessment || []}
            hiddenFields={formTemplate.hiddenBuiltInFields?.site_assessment || []}
            hiddenSections={formTemplate.hiddenSections?.site_assessment || []}
            sectionOrder={formTemplate.sectionOrder?.site_assessment || []}
            builtInFieldOrder={formTemplate.builtInFieldOrder?.site_assessment || []}
            onNavigateToPrevious={handleNavigateBack}
            onNavigateToNext={handleNavigateNext}
          />
        );
      case 'measure_specification':
        return (
          <MeasureSpecificationForm
            onNavigateNext={handleNavigateNext}
            onNavigateBack={handleNavigateBack}
            floorPlanId={floorPlanId}
            surveyData={surveyData}
            surveyId={currentSurveyId || tempSurveyId || actualSurveyId || undefined}
            onDataUpdate={updateSurveyData}
            onSaveImmediately={saveImmediately}
          />
        );
      case 'pas2030_questions':
        return (
          <PAS2030QuestionsFormSchemaVersion
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            customFields={formTemplate.customFieldsForBuiltInPages?.pas2030_questions || []}
            hiddenFields={formTemplate.hiddenBuiltInFields?.pas2030_questions || []}
            hiddenSections={formTemplate.hiddenSections?.pas2030_questions || []}
            sectionOrder={formTemplate.sectionOrder?.pas2030_questions || []}
            builtInFieldOrder={formTemplate.builtInFieldOrder?.pas2030_questions || []}
            onNavigateToPrevious={handleNavigateBack}
            onNavigateToNext={handleNavigateNext}
          />
        );
      case 'property_front':
        const exposedSide = surveyData?.property_front?.exposedSide;
        const initialExposedSide = (exposedSide === 'left' || exposedSide === 'right') ? exposedSide : '';
        return (
          <PropertyFrontForm
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            surveyId={currentSurveyId || tempSurveyId || surveyData?.id || actualSurveyId || undefined}
            floorPlanId={surveyData?.floor_plan?.floorPlanId}
            onNavigateBack={handleNavigateBack}
            onNavigateNext={handleNavigateNext}
            onBuildFormChange={handleBuildFormChange}
            initialBuildForm={surveyData?.property_front?.buildForm || ''}
            initialExposedSide={initialExposedSide}
            onGalleryStateChange={setIsGalleryOpen}
          />
        );
      case 'property_rear':
        return (
          <PropertyRearForm
            onNavigateToRooms={handleNavigateNext}
            onNavigateBackToPropertyFront={handleNavigateBack}
            floorPlanId={floorPlanId}
            surveyData={surveyData}
            surveyId={currentSurveyId || tempSurveyId || actualSurveyId || undefined}
            onDataUpdate={updateSurveyData}
            autosave={autosave}
            onSaveImmediately={saveImmediately}
            onGalleryStateChange={setIsGalleryOpen}
            customFields={formTemplate.customFieldsForBuiltInPages?.['property_rear'] || []}
            hiddenFields={formTemplate.hiddenBuiltInFields?.['property_rear'] || []}
            hiddenSections={formTemplate.hiddenSections?.['property_rear'] || []}
            sectionOrder={formTemplate.sectionOrder?.['property_rear']}
            builtInFieldOrder={formTemplate.builtInFieldOrder?.['property_rear']}
          />
        );
      case 'property_right':
        return (
          <PropertyRightForm
            onNavigateToRooms={handleNavigateNext}
            onNavigateBackToPropertyRear={handleNavigateBack}
            floorPlanId={floorPlanId}
            surveyData={surveyData}
            surveyId={currentSurveyId || tempSurveyId || actualSurveyId || undefined}
            onDataUpdate={updateSurveyData}
            autosave={autosave}
            onSaveImmediately={saveImmediately}
            onGalleryStateChange={setIsGalleryOpen}
            customFields={formTemplate.customFieldsForBuiltInPages?.['property_right'] || []}
            hiddenFields={formTemplate.hiddenBuiltInFields?.['property_right'] || []}
            hiddenSections={formTemplate.hiddenSections?.['property_right'] || []}
            sectionOrder={formTemplate.sectionOrder?.['property_right']}
            builtInFieldOrder={formTemplate.builtInFieldOrder?.['property_right']}
          />
        );
      case 'property_left':
        return (
          <PropertyLeftForm
            onNavigateToRooms={handleNavigateNext}
            onNavigateBackToPropertyRight={handleNavigateBack}
            floorPlanId={floorPlanId}
            surveyData={surveyData}
            surveyId={currentSurveyId || tempSurveyId || actualSurveyId || undefined}
            onDataUpdate={updateSurveyData}
            autosave={autosave}
            onSaveImmediately={saveImmediately}
            onGalleryStateChange={setIsGalleryOpen}
            customFields={formTemplate.customFieldsForBuiltInPages?.['property_left'] || []}
            hiddenFields={formTemplate.hiddenBuiltInFields?.['property_left'] || []}
            hiddenSections={formTemplate.hiddenSections?.['property_left'] || []}
            sectionOrder={formTemplate.sectionOrder?.['property_left']}
            builtInFieldOrder={formTemplate.builtInFieldOrder?.['property_left']}
          />
        );
      case 'room_details':
        return (
          <RoomSurveyContainer
            onNavigate={onNavigate}
            onNavigateNext={handleNavigateNext}
            onNavigateBack={handleNavigateBack}
            retrofitSurveyId={floorPlanId}
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            onGalleryStateChange={setIsGalleryOpen}
          />
        );
      case 'meters':
        return (
          <MetersFormSchemaVersion
            onNavigateNext={handleNavigateNext}
            onNavigateBack={handleNavigateBack}
            surveyData={surveyData}
            surveyId={currentSurveyId || tempSurveyId || undefined}
            onDataUpdate={updateSurveyData}
            onSaveImmediately={saveImmediately}
            onGalleryStateChange={setIsGalleryOpen}
            customFields={formTemplate.customFieldsForBuiltInPages?.['meters'] || []}
            hiddenFields={formTemplate.hiddenBuiltInFields?.['meters'] || []}
            hiddenSections={formTemplate.hiddenSections?.['meters'] || []}
            sectionOrder={formTemplate.sectionOrder?.['meters']}
            builtInFieldOrder={formTemplate.builtInFieldOrder?.['meters']}
          />
        );
      case 'main_property_measurements':
        return (
          <MainPropertyMeasurementsForm
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            surveyId={currentSurveyId || tempSurveyId || surveyData?.id || undefined}
            floorPlanId={surveyData?.floor_plan?.floorPlanId}
            onNavigateBack={handleNavigateBack}
            onNavigateNext={handleNavigateNext}
          />
        );
      case 'extensions':
        return (
          <ExtensionsForm
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            surveyId={currentSurveyId || tempSurveyId || surveyData?.id || undefined}
            floorPlanId={surveyData?.floor_plan?.floorPlanId}
            onNavigateBack={handleNavigateBack}
            onNavigateNext={handleNavigateNext}
          />
        );
      case 'signatures':
        return (
          <RetrofitSignaturesForm
            surveyData={surveyData}
            onDataUpdate={updateSurveyData}
            onNavigateBack={handleNavigateBack}
            onNavigateNext={handleNavigateNext}
          />
        );
      case 'export':
        // Debug: Log formTemplate before passing to export
        console.log('🔍 [Layout] formTemplate before export:', {
          id: formTemplate.id,
          hasCustomPages: !!formTemplate.customPages,
          customPagesCount: formTemplate.customPages?.length || 0,
          customPages: formTemplate.customPages?.map(cp => ({
            id: cp.id,
            label: cp.label,
            fieldsCount: cp.fields?.length || 0
          }))
        });
        
        return (
          <RetrofitExport
            onNavigateBack={() => setCurrentSection('signatures')}
            surveyData={surveyData}
            onModalStateChange={setIsExportModalOpen}
            onDataUpdate={updateSurveyData}
            onConvertPhotosToUrls={async () => {
              console.log('[RetrofitSurveyLayout] onConvertPhotosToUrls called');
              return { success: true, message: 'Photos already on CDN' };
            }}
            onSaveImmediately={async () => {
              console.log('[RetrofitSurveyLayout] onSaveImmediately called');
              await autosave.triggerManualSave();
              return currentSurveyId || tempSurveyId || '';
            }}
            formTemplate={formTemplate}
            onGetFreshFloorPlanData={() => {
              console.log('[RetrofitSurveyLayout] Getting fresh floor plan data for export');
              return surveyData.floor_plan;
            }}
            onSectionNavigate={(sectionId: string) => {
              console.log('[RetrofitSurveyLayout] Navigating to section from export:', sectionId);
              setCurrentSection(sectionId as SurveySectionId);
              // Scroll to top for better UX
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        );
      case 'floor_plan':
        return (
          <RetrofitFloorPlan
            key={`floor-plan-${currentSurveyId || tempSurveyId}`}
            onNavigate={onNavigate}
            floorPlanId={floorPlanId}
            surveyData={{
              ...surveyData,
              id: currentSurveyId || tempSurveyId || undefined
            }}
            onDataUpdate={updateSurveyData}
            onFullscreenChange={setIsFloorPlanFullscreen}
            onModalOpenChange={setIsFloorPlanModalOpen}
            onRegisterGetDataMethod={(fn) => { 
              console.log('🔗 [RetrofitSurveyLayout] Storing getDataToSave function in ref');
              floorPlanRef.current = fn;
            }}
          />
        );
      default:
        // Check if current section is a custom page (new structure)
        const newCustomPage = formTemplate.customPages?.find(p => p.id === currentSection);
        
        if (newCustomPage) {
          const activeSurveyId = currentSurveyId || tempSurveyId;
          
          // Convert CustomPage to PageSection format for DynamicFormRenderer
          const pageSection: typeof formTemplate.pages[0] = {
            id: newCustomPage.id,
            title: newCustomPage.label,
            fields: newCustomPage.fields,
            showInPDF: true,
            pdfOrder: 999
          };
          
          // Create temporary template with this custom page
          const tempTemplate: FormTemplate = {
            ...formTemplate,
            pages: [pageSection]
          };
          
          return (
            <div className="bg-white rounded-lg shadow-md p-6">
              <DynamicFormRenderer
                template={tempTemplate}
                pageId={newCustomPage.id}
                formData={surveyData.custom_fields?.[newCustomPage.id] || {}}
                onDataChange={(fieldId: string, value: any) => {
                  updateSurveyData((prev) => ({
                    ...prev,
                    custom_fields: {
                      ...(prev.custom_fields || {}),
                      [newCustomPage.id]: {
                        ...((prev.custom_fields || {})[newCustomPage.id] || {}),
                        [fieldId]: value
                      }
                    }
                  }));
                }}
                onPhotoUpload={async (fieldId: string, files: File[]) => {
                  // TODO: Implement photo upload for custom fields
                  console.log('Photo upload for custom field:', fieldId, files);
                  // await handlePhotoUpload(files, `${newCustomPage.id}_${fieldId}`);
                }}
                editMode={isEditMode}
                onTemplateChange={(updatedTemplate: FormTemplate) => {
                  // Update the custom page's fields
                  const updatedCustomPages = formTemplate.customPages?.map(p =>
                    p.id === newCustomPage.id
                      ? { ...p, fields: updatedTemplate.pages[0].fields }
                      : p
                  ) || [];
                  
                  const finalTemplate: FormTemplate = {
                    ...formTemplate,
                    customPages: updatedCustomPages,
                    updatedAt: new Date().toISOString()
                  };
                  
                  setFormTemplate(finalTemplate);
                  saveTemplateToDatabase(finalTemplate);
                }}
              />
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t-2 border-gray-200">
                <button
                  onClick={handleNavigateBack}
                  disabled={activeIndex === 0}
                  className={`px-6 py-3 rounded-xl font-medium flex items-center space-x-2 transition-all
                    ${activeIndex === 0
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white hover:from-gray-600 hover:to-gray-700 shadow-md hover:shadow-lg'
                    }`}
                >
                  <ChevronLeft size={20} />
                  <span>Previous</span>
                </button>
                <button
                  onClick={handleNavigateNext}
                  disabled={activeIndex === visibleSections.length - 1}
                  className={`px-6 py-3 rounded-xl font-medium flex items-center space-x-2 transition-all
                    ${activeIndex === visibleSections.length - 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg'
                    }`}
                >
                  <span>Next</span>
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          );
        }
        
        // Check if current section is a custom page from old template structure (fallback)
        const oldCustomPage = formTemplate.pages.find(p => p.id === currentSection);
        
        if (oldCustomPage) {
          const activeSurveyId = currentSurveyId || tempSurveyId;
          
          return (
            <div className="bg-white rounded-lg shadow-md p-6">
              <DynamicFormRenderer
                template={formTemplate}
                pageId={currentSection}
                formData={surveyData.custom_fields || {}}
                onDataChange={(fieldId: string, value: any) => {
                  updateSurveyData((prev) => ({
                    ...prev,
                    custom_fields: {
                      ...(prev.custom_fields || {}),
                      [fieldId]: value
                    }
                  }));
                }}
                editMode={isEditMode}
                onTemplateChange={(updatedTemplate: FormTemplate) => {
                  setFormTemplate(updatedTemplate);
                  // Auto-save template changes
                  if (activeSurveyId) {
                    saveFormTemplate(updatedTemplate, activeSurveyId).then((result) => {
                      if (result.success) {
                        console.log('✅ Form template saved for survey:', activeSurveyId);
                      } else {
                        console.error('❌ Error saving form template:', result.error);
                      }
                    });
                  }
                }}
              />
            </div>
          );
        }
        
        // Truly unknown section
        return <p>Unknown section: {currentSection}</p>;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-100 via-white to-gray-100 overflow-x-hidden">
      {/* Main Header for the entire survey layout - Hide when address modal is showing, floor plan is fullscreen, gallery is open, or export modal is open */}
      {!showAddressModal && !isFloorPlanFullscreen && !isGalleryOpen && !isExportModalOpen && (
        <header className={`bg-white shadow-md sticky top-0 z-[100] flex-shrink-0 header-safe-area rounded-b-2xl border-b-2 border-yellow-400 transition-all duration-200 ${isFloorPlanModalOpen ? 'blur-sm pointer-events-none' : ''}`}>
          <div className="w-full px-3 sm:px-6 py-3 sm:py-4">
            {/* Top Row: Back button, Title, Autosave */}
            <div className="flex items-center justify-between mb-2 sm:mb-0">
              <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                <button
                  onClick={() => onNavigate({ page: 'retrofit' })}
                  className="p-1.5 sm:p-2 text-gray-700 hover:bg-yellow-100 rounded-xl transition-colors flex-shrink-0"
                  aria-label="Back to Retrofit Menu"
                >
                  <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
                </button>
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <div className="bg-yellow-100 rounded-xl p-1.5 sm:p-2 flex-shrink-0">
                    <ListChecks size={16} className="text-yellow-600 sm:w-5 sm:h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h1 className="text-sm sm:text-lg font-bold text-gray-800 truncate">{surveyTitle}</h1>
                  </div>
                </div>
              </div>
              
              {/* Autosave indicator - always visible on right */}
              <div className="flex-shrink-0 ml-2">
                <AutosaveIndicator 
                  status={autosave.autosaveStatus}
                  lastSavedAt={autosave.lastSavedAt}
                  lastError={autosave.lastError}
                  retryCount={autosave.retryCount}
                />
              </div>
            </div>
            
            {/* Bottom Row: Action buttons - Only on non-mobile OR collapsed to icons on mobile */}
            <div className="flex items-center justify-end gap-2 sm:gap-3 mt-2 sm:mt-0">
              {/* Add Page Button - Hidden for standard plan users */}
              {!isStandardPlan && (
                <button
                  onClick={() => setIsPageModalOpen(true)}
                  className="px-2 py-1.5 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 flex items-center gap-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md hover:from-green-600 hover:to-green-700"
                  title="Create a new custom page"
                >
                  <Plus size={14} className="sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Add Page</span>
                </button>
              )}
              
              {/* Edit Mode Toggle - Works on all pages: hide/add fields on built-in, full edit on custom - Hidden for standard plan users */}
              {!isReorderMode && !isStandardPlan && (
                <button
                  onClick={() => {
                    console.log('[Edit Button] Clicked - Current section:', currentSection, 'Current edit mode:', isEditMode);
                    console.log('[Edit Button] Built-in sections available:', getBuiltInFieldSections(currentSection).length);
                    console.log('[Edit Button] Built-in fields available:', getBuiltInFieldDefinitions(currentSection).length);
                    setIsEditMode(!isEditMode);
                  }}
                  className={`px-2 py-1.5 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    isEditMode
                      ? 'bg-blue-500 text-white shadow-md hover:bg-blue-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={isEditMode ? 'Exit Edit Mode' : 'Edit Form (Hide fields / Add custom fields)'}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14"
                    className="sm:w-4 sm:h-4"
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    <path d="m15 5 4 4" />
                  </svg>
                  <span className="hidden sm:inline">{isEditMode ? 'Editing' : 'Edit'}</span>
                </button>
              )}
              
              {/* Exit Reorder Mode Button - Only show when IN reorder mode */}
              {isReorderMode && (
                <button
                  onClick={handleExitReorderMode}
                  className="px-2 py-1.5 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 flex items-center gap-1.5 bg-green-500 text-white shadow-md hover:bg-green-600"
                  title="Exit reorder mode"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14"
                    className="sm:w-4 sm:h-4"
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span className="hidden sm:inline">Done Reordering</span>
                </button>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Section Navigation Bar - Hide when floor plan is fullscreen, gallery is open, or export modal is open */}
      {!isFloorPlanFullscreen && !isGalleryOpen && !isExportModalOpen && (
      <div className={`bg-white shadow-lg sticky z-[90] border-b-2 border-yellow-300 overflow-hidden transition-all duration-200 ${showAddressModal ? 'top-0' : 'top-[88px]'} ${isFloorPlanModalOpen ? 'blur-sm pointer-events-none' : ''}`}>
        <div className="container mx-auto px-4 sm:px-6 py-3">
          <div 
            className="flex items-center space-x-2 overflow-x-auto overflow-y-hidden scrollbar-hide"
            style={{
              scrollbarWidth: 'none' as any, /* Firefox */
              msOverflowStyle: 'none' as any, /* IE and Edge */
              WebkitOverflowScrolling: 'touch' as any, /* Smooth scrolling on iOS */
            }}
          >
            <div className="flex items-center space-x-1 min-w-0 flex-1">
              {visibleSections.map((section, index) => {
                const IconComponent = section.icon;
                const isActive = currentSection === section.id;
                const isCompleted = visibleSections.findIndex(s => s.id === currentSection) > index;
                
                return (
                  <React.Fragment key={section.id}>
                    {/* Page button with integrated arrows */}
                    <div
                      className={`flex items-center rounded-lg transition-all duration-150 shadow-sm
                        ${isActive && !isReorderMode
                          ? 'bg-yellow-400 text-white shadow-md scale-105' 
                          : isCompleted && !isReorderMode
                          ? 'bg-green-100 text-green-700'
                          : isReorderMode
                          ? 'bg-purple-50 text-purple-700 border-2 border-purple-300'
                          : 'text-gray-600'
                        }`}
                    >
                      {/* Reorder arrows - inside the page box */}
                      {isReorderMode && (
                        <div className="flex flex-col gap-0.5 px-1 border-r border-purple-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMovePageLeft(index);
                            }}
                            disabled={index === 0}
                            className={`p-0.5 rounded transition-colors ${
                              index === 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-purple-600 hover:bg-purple-200'
                            }`}
                            title="Move left"
                          >
                            <ChevronLeft size={12} strokeWidth={3} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMovePageRight(index);
                            }}
                            disabled={index === visibleSections.length - 1}
                            className={`p-0.5 rounded transition-colors ${
                              index === visibleSections.length - 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-purple-600 hover:bg-purple-200'
                            }`}
                            title="Move right"
                          >
                            <ChevronRight size={12} strokeWidth={3} />
                          </button>
                        </div>
                      )}
                      
                      {/* Page content */}
                      <button
                        onClick={() => !isReorderMode && setCurrentSection(section.id)}
                        title={section.label}
                        className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0
                          ${!isReorderMode && (isActive || isCompleted) ? 'hover:brightness-95' : ''}
                          ${!isReorderMode && !isActive && !isCompleted ? 'hover:bg-yellow-50 hover:text-yellow-600' : ''}`}
                      >
                        <IconComponent size={14} className="flex-shrink-0 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-medium">{section.label}</span>
                        {section.isCustom && !isReorderMode && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete page "${section.label}"?`)) {
                                handleDeletePage(section.id);
                              }
                            }}
                            className="ml-1 text-red-500 hover:text-red-700 opacity-70 hover:opacity-100"
                            title="Delete custom page"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </button>
                    </div>
                    
                    {index < visibleSections.length - 1 && !isReorderMode && (
                      <ChevronRight 
                        size={16} 
                        className={`text-gray-400 flex-shrink-0 ${isCompleted ? 'text-green-400' : ''}`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Reorder Mode Banner */}
      {isReorderMode && !isFloorPlanFullscreen && !isGalleryOpen && !isExportModalOpen && (
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 px-4 sm:px-6 shadow-lg sticky z-[89]" style={{ top: showAddressModal ? '64px' : '152px' }}>
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <LayoutGrid size={20} />
              <div>
                <p className="font-semibold text-sm sm:text-base">Page Reorder Mode Active</p>
                <p className="text-xs sm:text-sm opacity-90">Use the arrow buttons to reorder pages. Click "Done Reordering" when finished.</p>
              </div>
            </div>
            <button
              onClick={handleExitReorderMode}
              className="px-4 py-2 bg-white text-purple-700 rounded-lg font-medium text-sm hover:bg-purple-50 transition-colors flex items-center gap-2"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span className="hidden sm:inline">Done</span>
            </button>
          </div>
        </div>
      )}

      {/* Address Input Modal */}
      <RetrofitAddressInputModal
        isOpen={showAddressModal}
        onAddressSubmit={handleAddressSubmit}
        onSkip={() => onNavigate({ page: 'retrofit' })} // Force return to main menu instead of skipping
        title="Enter Property Address"
        description="Please enter the property address for this retrofit assessment survey."
        allowSkip={false} // Disable skip functionality
        initialAddress={{
          streetAddress: surveyData.initial_questionnaire.streetAddress,
          town: surveyData.initial_questionnaire.town,
          postCode: surveyData.initial_questionnaire.postCode
        }}
      />

      {/* Main Content Area - Now full width */}
      <div className="flex-1 container mx-auto mb-4 px-4 sm:px-0">
        <main className="w-full overflow-visible">
          {/* Loading state */}
          {isDataLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-lg text-gray-600">Loading survey data...</div>
            </div>
          ) : (
            /* Content will be rendered here based on activeSection */
            /* The individual forms will have their own white bg, padding, shadow etc. */
            renderSection()
          )}
        </main>
      </div>

      {/* Form Editor Side Panel */}
      <FormEditorPanel
        key={`panel-${currentSection}-${JSON.stringify(formTemplate.builtInFieldOrder?.[currentSection] || [])}-${formTemplate.customPages?.find(p => p.id === currentSection)?.fields.length || 0}`}
        isOpen={isEditMode}
        onClose={() => {
          console.log('[FormEditorPanel] Closing form editor for section:', currentSection);
          setIsEditMode(false);
        }}
        sectionId={currentSection}
        sectionTitle={visibleSections.find((s: SurveySection) => s.id === currentSection)?.label || 'Form Editor'}
        allFields={(() => {
          const fields = getAllFieldsForSection(currentSection);
          console.log('[FormEditorPanel] All fields for', currentSection, ':', fields);
          return fields;
        })()}
        builtInFields={[]} // Kept for backwards compatibility but unused
        builtInFieldSections={(() => {
          const sections = getBuiltInFieldSections(currentSection);
          console.log('[FormEditorPanel] Built-in field sections for', currentSection, ':', sections);
          return sections;
        })()}
        customFields={(() => {
          // Check if this is a custom page
          const customPage = formTemplate.customPages?.find(p => p.id === currentSection);
          if (customPage) {
            console.log('[FormEditorPanel] Custom page fields:', customPage.fields);
            return customPage.fields;
          }
          // Otherwise use custom fields for built-in pages
          return formTemplate.customFieldsForBuiltInPages?.[currentSection] || [];
        })()}
        hiddenFields={formTemplate.hiddenBuiltInFields?.[currentSection] || []}
        hiddenSections={formTemplate.hiddenSections?.[currentSection] || []}
        sectionOrder={formTemplate.sectionOrder?.[currentSection]}
        onToggleFieldVisibility={handleToggleFieldVisibility}
        onToggleSectionVisibility={handleToggleSectionVisibility}
        onAddCustomField={handleAddCustomField}
        onEditCustomField={handleEditCustomField}
        onDeleteCustomField={handleDeleteCustomField}
        onEnterReorderMode={handleEnterReorderMode}
        isReorderMode={isReorderMode}
        onReorderCustomFields={(from, to) => {
          // TODO: Implement reordering for custom fields
          console.log('Reorder custom fields:', from, to);
        }}
        onReorderBuiltInFields={handleReorderBuiltInFields}
        onReorderSections={handleReorderSections}
        onReorderFieldsInSection={handleReorderFieldsInSection}
      />

      {/* Field Configuration Modal */}
      <FieldConfigModal
        field={editingField}
        isOpen={isFieldModalOpen}
        onClose={() => {
          setIsFieldModalOpen(false);
          setEditingField(null);
        }}
        onSave={handleSaveField}
        existingSections={getExistingSections(currentSection)}
      />

      {/* Page Management Modal */}
      <PageManagementModal
        isOpen={isPageModalOpen}
        onClose={() => setIsPageModalOpen(false)}
        onCreatePage={handleCreatePage}
        existingPageNames={[
          ...allSections.map(s => s.label),
          ...(formTemplate.customPages || []).map(p => p.label)
        ]}
      />
    </div>
  );
} 